//import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";

import { auth, db } from "../firebase";
import {
  collection, addDoc, getDocs, getDoc, doc,
  setDoc, deleteDoc, updateDoc, increment,
  query, orderBy, serverTimestamp, onSnapshot,
  limit, startAfter, where
} from "firebase/firestore";
import DOMPurify from "dompurify";

const PAGE_SIZE = 20;

/* ─── Mobile hook ───────────────────────────────────────────────────────────
   Single source of truth for responsive layout decisions.
   Threshold: 640px matches the CSS @media breakpoint below.
*/
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return isMobile;
}

/* ══════════════════════════════════════════════
   BANNED WORDS — UNCHANGED
══════════════════════════════════════════════ */
const BANNED_WORDS = [
  "fuck","fucker","fucking","fucked","fucks","fuk","fck","phuck","fuuck",
  "shit","shitting","shits","shitty","shite","bitch","bitches","bich","biatch","byatch",
  "slut","sluts","whore","whores","asshole","assholes","bastard","bastards",
  "cunt","cunts","dick","dicks","dik","diq","cock","cocks","cok","pussy","pussies",
  "nigger","nigga","niggar","faggot","fag","retard","retarded","retawd",
  "motherfucker","motherfucking","rape","raping","raper",
  "kys","kill yourself","go kill yourself",
  "bhenchod","behenchod","behanchod","bhench","bhenchodd","bahenchod",
  "madarchod","madarch","madarchodd","madarchoot","madarchut","maadarchod",
  "bhosda","bhosdaa","bhosdike","bhosdiwala","bhosdiwale","bsdk","b.s.d.k.","bosadike","bosdike",
  "chutiya","chutiye","chutia","chutiyapa","chutiyaa","gandu","ganduu","gaandu",
  "lund","loda","lauda","laude","lode","loduu","chut","choot","chooot","buur","burr",
  "randi","raand","randy","randwa","harami","haraami","haramkhor","haramzada","haramjaada",
  "kutta","kutte","kutiya","kuttiya","kutti","suar","bhadwa","bhaduaa","bhadva","bhadve",
  "chodu","chodd","chud","chudai","chudne","chudwa","gand","gaand","gandi","moot",
  "chooche","chuchi","choochi","mammey","mamme","bakchod","bakchodd","kamina","kameena","kameene",
  "jhatu","jhat","jhaantu","laundi","laundiya","launda","fck","fucc","phuq",
  "balatkar","balatkari","balatkaar",
];
function normaliseLeet(t){return t.replace(/@/g,"a").replace(/\$/g,"s").replace(/\*/g,"").replace(/!/g,"i").replace(/0/g,"o").replace(/1/g,"i").replace(/3/g,"e").replace(/4/g,"a").replace(/5/g,"s").replace(/8/g,"b");}
function containsBannedWords(html){const raw=html.replace(/<[^>]*>/g," ").toLowerCase();const plain=normaliseLeet(raw).replace(/[^a-z\s.]/g,"");return BANNED_WORDS.filter(w=>plain.includes(normaliseLeet(w.toLowerCase())));}
function countWords(html){const plain=html.replace(/<[^>]*>/g," ").replace(/&nbsp;/g," ").trim();return plain===""?0:plain.split(/\s+/).filter(Boolean).length;}
function sanitize(html){return DOMPurify.sanitize(html,{USE_PROFILES:{html:true}});}

/* ══════════════════════════════════════════════
   THEME CONFIG
══════════════════════════════════════════════ */
export const STORY_THEMES = [
  {id:"real-journey",  label:"Real Life Journey",emoji:"🌱",color:"#3D6B57",light:"#EAF3EE",editorBg:"#F2FAF5",textColor:"#2A4F3E"},
  {id:"motivational",  label:"Motivational",     emoji:"🔥",color:"#B8421E",light:"#FAE9E4",editorBg:"#FDF4F1",textColor:"#8A2E12"},
  {id:"fiction",       label:"Fiction",           emoji:"📖",color:"#5C4E9A",light:"#EDEBF9",editorBg:"#F6F4FD",textColor:"#3D3274"},
  {id:"horror",        label:"Horror",            emoji:"👻",color:"#1E1E1E",light:"#EBEBEB",editorBg:"#F5F5F5",textColor:"#1E1E1E"},
  {id:"romance",       label:"Romance",           emoji:"💕",color:"#A83860",light:"#FAE8F0",editorBg:"#FDF2F6",textColor:"#7A2444"},
  {id:"mental-health", label:"Mental Health",     emoji:"🧠",color:"#2D7E72",light:"#DFF2EF",editorBg:"#EFF9F7",textColor:"#1D5E55"},
  {id:"comedy",        label:"Comedy",            emoji:"😄",color:"#B07A10",light:"#FBF0D4",editorBg:"#FFFBEE",textColor:"#7A5508"},
  {id:"self-discovery",label:"Self Discovery",    emoji:"✨",color:"#3A5E9C",light:"#E4EDF9",editorBg:"#F1F6FD",textColor:"#273E6B"},
];

const TEXT_COLORS = [
  {label:"Default",    value:"inherit"},{label:"Terracotta",value:"#B8421E"},
  {label:"Forest",     value:"#3D6B57"},{label:"Violet",    value:"#5C4E9A"},
  {label:"Amber",      value:"#B07A10"},{label:"Rose",       value:"#A83860"},
  {label:"Sapphire",   value:"#3A5E9C"},{label:"Obsidian",   value:"#1E1E1E"},
];
const FONT_SIZES = [{label:"S",value:"13px"},{label:"M",value:"16px"},{label:"L",value:"20px"},{label:"XL",value:"26px"}];

/* ══════════════════════════════════════════════
   ABSTRACT COVER ART
══════════════════════════════════════════════ */
function RealJourneyArt({color}){
  return(
    <svg viewBox="0 0 380 200" preserveAspectRatio="xMidYMid slice" style={{width:"100%",height:"100%"}}>
      {[200,175,150,125,100,75,50].map((r,i)=>(
        <ellipse key={i} cx={190} cy={260} rx={r*1.4} ry={r}
          fill="none" stroke={color} strokeWidth={0.7}
          strokeOpacity={0.08+i*0.04}
          strokeDasharray={`${r*1.8} ${r*0.4}`} strokeDashoffset={i*18}/>
      ))}
      <path d="M190,40 C210,20 230,30 220,10 C200,25 185,15 190,40Z" fill={color} opacity={0.22}/>
      <path d="M190,40 C170,20 150,30 160,10 C178,25 193,15 190,40Z" fill={color} opacity={0.14}/>
      <line x1="190" y1="40" x2="190" y2="130" stroke={color} strokeWidth={1} strokeOpacity={0.18}/>
    </svg>
  );
}
function MotivationalArt({color}){
  return(
    <svg viewBox="0 0 380 200" preserveAspectRatio="xMidYMid slice" style={{width:"100%",height:"100%"}}>
      {[0,1,2,3,4,5,6,7].map(i=>(
        <line key={i} x1={40+i*44} y1={200} x2={40+i*44-60} y2={-10}
          stroke={color} strokeWidth={1.2} strokeOpacity={0.07+i*0.015}/>
      ))}
      <polygon points="110,180 190,40 270,180" fill={color} opacity={0.15}/>
      <polygon points="50,180 190,10 330,180" fill="none" stroke={color} strokeWidth={1.2} strokeOpacity={0.22}/>
      <polygon points="80,180 190,60 300,180" fill="none" stroke={color} strokeWidth={0.8} strokeOpacity={0.12}/>
      <circle cx={190} cy={10} r={6} fill={color} opacity={0.35}/>
    </svg>
  );
}
function FictionArt({color}){
  return(
    <svg viewBox="0 0 380 200" preserveAspectRatio="xMidYMid slice" style={{width:"100%",height:"100%"}}>
      {[[120,60,80,110],[200,40,110,130],[160,100,90,100],[240,80,70,90],[100,130,60,80],[280,110,80,100]].map(([x,y,w,h],i)=>(
        <rect key={i} x={x-w/2} y={y-h/2} width={w} height={h} rx={3}
          fill={color} opacity={0.04+i*0.025}
          transform={`rotate(${-15+i*8},${x},${y})`}/>
      ))}
      {[[120,60],[200,40],[160,100],[240,80],[100,130],[280,110]].map(([x,y],i)=>(
        <rect key={i} x={x-1} y={y-1} width={2} height={2} fill={color} opacity={0.5}/>
      ))}
      <path d="M60,200 L320,0" stroke={color} strokeWidth={0.5} strokeOpacity={0.12}/>
    </svg>
  );
}
function HorrorArt({color}){
  return(
    <svg viewBox="0 0 380 200" preserveAspectRatio="xMidYMid slice" style={{width:"100%",height:"100%"}}>
      <path d="M0,200 L60,80 L120,140 L160,20 L200,100 L240,40 L280,120 L320,30 L380,200Z" fill={color} opacity={0.06}/>
      <path d="M0,200 L80,100 L140,160 L190,50 L230,130 L280,60 L380,200Z" fill={color} opacity={0.04}/>
      {[0,1,2,3,4,5].map(i=>(
        <line key={i} x1={i*76} y1={0} x2={380-i*40} y2={200}
          stroke={color} strokeWidth={0.6} strokeOpacity={0.08+i*0.02}/>
      ))}
      <circle cx={190} cy={100} r={40} fill="none" stroke={color} strokeWidth={0.8} strokeOpacity={0.15}/>
      <circle cx={190} cy={100} r={20} fill="none" stroke={color} strokeWidth={0.5} strokeOpacity={0.1}/>
    </svg>
  );
}
function RomanceArt({color}){
  return(
    <svg viewBox="0 0 380 200" preserveAspectRatio="xMidYMid slice" style={{width:"100%",height:"100%"}}>
      {[[130,90,100,70],[250,110,90,65],[190,60,80,55],[160,140,70,50],[210,140,75,52]].map(([cx,cy,rx,ry],i)=>(
        <ellipse key={i} cx={cx} cy={cy} rx={rx} ry={ry}
          fill={color} opacity={0.06+i*0.02}
          transform={`rotate(${i*24},${cx},${cy})`}/>
      ))}
      {[[130,90,100,70],[250,110,90,65],[190,60,80,55]].map(([cx,cy,rx,ry],i)=>(
        <ellipse key={i} cx={cx} cy={cy} rx={rx} ry={ry}
          fill="none" stroke={color} strokeWidth={0.8} strokeOpacity={0.18}
          transform={`rotate(${i*24},${cx},${cy})`}/>
      ))}
      <path d="M190,80 C180,65 155,65 155,85 C155,105 190,120 190,120 C190,120 225,105 225,85 C225,65 200,65 190,80Z" fill={color} opacity={0.25}/>
    </svg>
  );
}
function MentalHealthArt({color}){
  return(
    <svg viewBox="0 0 380 200" preserveAspectRatio="xMidYMid slice" style={{width:"100%",height:"100%"}}>
      {[90,75,60,45,30,15].map((r,i)=>(
        <circle key={i} cx={190} cy={100} r={r}
          fill="none" stroke={color} strokeWidth={0.8}
          strokeOpacity={0.1+i*0.04}
          strokeDasharray={i%2===0?"4 4":"none"}/>
      ))}
      {[0,30,60,90,120,150].map((angle,i)=>{
        const rad=angle*Math.PI/180;
        return(
          <line key={i} x1={190+90*Math.cos(rad)} y1={100+90*Math.sin(rad)}
            x2={190+110*Math.cos(rad)} y2={100+110*Math.sin(rad)}
            stroke={color} strokeWidth={1} strokeOpacity={0.2}/>
        );
      })}
      <circle cx={190} cy={100} r={8} fill={color} opacity={0.3}/>
      <circle cx={190} cy={100} r={4} fill={color} opacity={0.5}/>
    </svg>
  );
}
function ComedyArt({color}){
  return(
    <svg viewBox="0 0 380 200" preserveAspectRatio="xMidYMid slice" style={{width:"100%",height:"100%"}}>
      {[[60,40,14],[120,80,10],[180,30,18],[240,70,12],[300,45,16],[80,150,8],[160,130,14],[220,160,10],[310,140,12],[350,80,6]].map(([x,y,r],i)=>(
        <circle key={i} cx={x} cy={y} r={r}
          fill={color} opacity={0.06+i*0.015}/>
      ))}
      {[[60,40,14],[180,30,18],[300,45,16],[160,130,14]].map(([x,y,r],i)=>(
        <circle key={i} cx={x} cy={y} r={r}
          fill="none" stroke={color} strokeWidth={0.8} strokeOpacity={0.25}/>
      ))}
      <path d="M90,100 Q150,60 190,80 Q230,100 290,70" fill="none" stroke={color} strokeWidth={1.5} strokeOpacity={0.22} strokeLinecap="round"/>
    </svg>
  );
}
function SelfDiscoveryArt({color}){
  return(
    <svg viewBox="0 0 380 200" preserveAspectRatio="xMidYMid slice" style={{width:"100%",height:"100%"}}>
      {Array.from({length:5},(_,i)=>{
        const r=20+i*28; const angle=i*137.5*Math.PI/180;
        return(
          <g key={i}>
            <circle cx={190+r*Math.cos(angle)} cy={100+r*Math.sin(angle)*0.6} r={8-i}
              fill={color} opacity={0.08+i*0.04}/>
          </g>
        );
      })}
      {[50,75,100,125,150].map((r,i)=>(
        <path key={i} d={`M ${190+r} ${100} A ${r} ${r*0.7} 0 0 1 ${190-r} ${100}`}
          fill="none" stroke={color} strokeWidth={0.7} strokeOpacity={0.1+i*0.04}/>
      ))}
      <circle cx={190} cy={100} r={5} fill={color} opacity={0.5}/>
      {[0,72,144,216,288].map((a,i)=>{
        const rad=a*Math.PI/180;
        return <line key={i} x1={190} y1={100} x2={190+55*Math.cos(rad)} y2={100+38*Math.sin(rad)}
          stroke={color} strokeWidth={0.6} strokeOpacity={0.18}/>;
      })}
    </svg>
  );
}

function ThemeArt({theme, height=180}){
  const artMap = {
    "real-journey":   <RealJourneyArt   color={theme.color}/>,
    "motivational":   <MotivationalArt  color={theme.color}/>,
    "fiction":        <FictionArt       color={theme.color}/>,
    "horror":         <HorrorArt        color={theme.color}/>,
    "romance":        <RomanceArt       color={theme.color}/>,
    "mental-health":  <MentalHealthArt  color={theme.color}/>,
    "comedy":         <ComedyArt        color={theme.color}/>,
    "self-discovery": <SelfDiscoveryArt color={theme.color}/>,
  };
  return(
    <div style={{height,background:theme.light,overflow:"hidden",position:"relative",flexShrink:0}}>
      {artMap[theme.id] || <MentalHealthArt color={theme.color}/>}
    </div>
  );
}

/* ══════════════════════════════════════════════
   GLOBAL STYLES
   FIXES:
   1. .p-story-card / .p-story-card:hover  — CSS-only hover eliminates JS
      state + transform blink loop.
   2. .p-prose drop cap — removed float + line-height:.74 which rendered as
      a black box on iOS Safari/Chrome when content is <div> (contentEditable
      default). Replaced with a safe inline-block approach that degrades
      gracefully when there is no <p> first-child.
   3. @media (max-width:640px) — mobile-first responsive rules covering modal,
      reading column, featured card grid, toolbar wrapping, font sizes.
══════════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500&family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,400;1,600&family=Outfit:wght@300;400;500;600;700&display=swap');

:root {
  --p-bg:          #FDFCF9;
  --p-surface:     #FFFFFF;
  --p-warm:        #F7F3EE;
  --p-ink:         #161412;
  --p-ink-2:       #363028;
  --p-ink-3:       #6B6258;
  --p-muted:       #A09890;
  --p-rule:        #E2D8CE;
  --p-rule-s:      #EDE8E2;
  --p-accent:      #B8421E;
  --p-accent-l:    #FAE9E4;
  --p-cormorant:   'Cormorant Garamond', Georgia, serif;
  --p-playfair:    'Playfair Display', Georgia, serif;
  --p-outfit:      'Outfit', system-ui, sans-serif;
  --p-sh-xs:       0 1px 4px rgba(22,20,18,.04);
  --p-sh-sm:       0 2px 14px rgba(22,20,18,.06), 0 1px 3px rgba(22,20,18,.03);
  --p-sh-md:       0 4px 32px rgba(22,20,18,.08), 0 2px 8px rgba(22,20,18,.04);
  --p-sh-lg:       0 16px 56px rgba(22,20,18,.12), 0 4px 16px rgba(22,20,18,.05);
  --p-sh-xl:       0 28px 80px rgba(22,20,18,.18), 0 8px 24px rgba(22,20,18,.07);
  --p-r-sm:        10px;
  --p-r-md:        16px;
  --p-r-lg:        22px;
}

.p-grain {
  position: fixed; inset: 0; z-index: 9998; pointer-events: none;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  opacity: 0.028;
  mix-blend-mode: multiply;
}

@keyframes pFadeUp    { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
@keyframes pFadeIn    { from{opacity:0} to{opacity:1} }
@keyframes pSlideUp   { from{opacity:0;transform:translateY(28px) scale(.985)} to{opacity:1;transform:translateY(0) scale(1)} }
@keyframes pHeartBeat { 0%,100%{transform:scale(1)} 30%{transform:scale(1.4)} 55%{transform:scale(1.12)} 75%{transform:scale(1.28)} }
@keyframes pShimmer   { 0%{background-position:-600px 0} 100%{background-position:600px 0} }

.p-skel {
  background: linear-gradient(90deg, var(--p-rule-s) 25%, var(--p-rule) 50%, var(--p-rule-s) 75%);
  background-size: 1200px 100%;
  animation: pShimmer 2s ease infinite;
  border-radius: 3px;
}

.p-card-enter { animation: pFadeUp .45s ease both; }
.p-card-enter:nth-child(1) {animation-delay:.04s}
.p-card-enter:nth-child(2) {animation-delay:.10s}
.p-card-enter:nth-child(3) {animation-delay:.16s}
.p-card-enter:nth-child(4) {animation-delay:.22s}
.p-card-enter:nth-child(5) {animation-delay:.28s}
.p-card-enter:nth-child(n+6){animation-delay:.34s}

.p-overlay { animation: pFadeIn .22s ease both; }
.p-modal   { animation: pSlideUp .38s cubic-bezier(.16,.84,.44,1) both; }

/* ─── Story card hover — CSS-only, no JS state ───────────────────────────────
   FIX: The old JS onMouseEnter/onMouseLeave + translateY(-4px) caused an
   infinite blink: moving the card upward shifted it away from the cursor,
   firing onMouseLeave, snapping it back, re-triggering onMouseEnter, repeat.
   CSS :hover never suffers from this because the browser handles it natively
   during the same paint cycle as the transform.
*/
.p-story-card {
  transition: transform .22s ease, box-shadow .22s ease;
  box-shadow: var(--p-sh-sm);
  will-change: transform;
}
.p-story-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--p-sh-lg);
}
/* On touch devices there is no real hover — disable the lift so cards don't
   get stuck in the lifted state after a tap. */
@media (hover: none) {
  .p-story-card:hover {
    transform: none;
    box-shadow: var(--p-sh-sm);
  }
}

.p-t-display {
  font-family: var(--p-cormorant);
  font-weight: 700;
  letter-spacing: -.02em;
  line-height: 1.1;
  color: var(--p-ink);
}
.p-t-heading {
  font-family: var(--p-playfair);
  font-weight: 700;
  letter-spacing: -.015em;
  line-height: 1.25;
  color: var(--p-ink);
}
.p-t-label {
  font-family: var(--p-outfit);
  font-size: .66rem;
  font-weight: 700;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: var(--p-muted);
}
.p-t-body {
  font-family: var(--p-outfit);
  font-size: .88rem;
  line-height: 1.7;
  color: var(--p-ink-3);
}

/* ─── Reading prose ──────────────────────────────────────────────────────────
   FIX: The original float-based drop cap (float:left; line-height:.74) renders
   as a solid black rectangle on iOS Safari / Chrome when the first child is a
   <div> (which contentEditable produces by default instead of <p>).  It also
   fails entirely when the first block has only a single character or is empty.
   Replaced with a simpler ::first-letter that uses display:inline-block and a
   safe line-height, which degrades gracefully across all browsers and content
   structures.
*/
.p-prose {
  font-family: var(--p-cormorant);
  font-size: 1.22rem;
  line-height: 2.0;
  color: var(--p-ink-2);
  letter-spacing: .008em;
}
.p-prose p  { margin: 0 0 1.6em 0; }
.p-prose div { margin: 0 0 1.2em 0; }

/* Safe drop cap: no float, no sub-1 line-height */
.p-prose > p:first-of-type::first-letter,
.p-prose > div:first-child::first-letter {
  font-family: var(--p-cormorant);
  font-size: 3.4rem;
  font-weight: 700;
  line-height: 1;
  float: left;
  margin: 4px 8px -4px 0;
  color: var(--drop-color, var(--p-accent));
}

.p-prose blockquote {
  margin: 32px 0;
  padding: 0 0 0 28px;
  border-left: 2px solid var(--p-rule);
  font-style: italic;
  font-size: 1.32rem;
  color: var(--p-ink-3);
}
.p-prose strong { font-weight: 700; color: var(--p-ink); }
.p-prose ul, .p-prose ol { padding-left: 28px; margin-bottom: 1.6em; }
.p-prose li { margin-bottom: .6em; }
.p-prose img { max-width: 100%; border-radius: 12px; margin: 24px 0; }

.p-pull-quote {
  font-family: var(--p-cormorant);
  font-size: 1.7rem;
  font-style: italic;
  font-weight: 300;
  color: var(--p-ink);
  line-height: 1.4;
  text-align: center;
  padding: 36px 20px;
  margin: 40px 0;
  position: relative;
}
.p-pull-quote::before, .p-pull-quote::after {
  content: '';
  display: block;
  height: 1px;
  background: var(--p-rule);
  margin: 16px auto;
  width: 80px;
}

.p-ornament {
  text-align: center;
  font-size: 1.5rem;
  color: var(--p-muted);
  margin: 48px 0 32px;
  letter-spacing: .3em;
  opacity: .5;
}

.p-comment-input {
  width: 100%; padding: 14px 18px;
  border-radius: var(--p-r-sm);
  border: 1px solid var(--p-rule);
  background: var(--p-warm);
  color: var(--p-ink);
  font-family: var(--p-outfit);
  font-size: .92rem;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
  line-height: 1.7;
  min-height: 88px;
  transition: border-color .18s, background .18s;
}
.p-comment-input:focus { border-color: var(--p-ink-3); background: var(--p-surface); }

.p-masthead-rule {
  display: flex; align-items: center; gap: 16px;
  margin-bottom: 20px;
}
.p-masthead-rule::before, .p-masthead-rule::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--p-rule);
}

.p-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 11px 24px;
  border-radius: 100px;
  font-family: var(--p-outfit);
  font-weight: 600;
  font-size: .86rem;
  letter-spacing: .01em;
  cursor: pointer;
  transition: all .18s ease;
  border: none;
}
.p-btn-ink {
  background: var(--p-ink);
  color: var(--p-bg);
  box-shadow: 0 2px 14px rgba(22,20,18,.22);
}
.p-btn-ink:hover {
  background: var(--p-ink-2);
  transform: translateY(-2px);
  box-shadow: 0 6px 22px rgba(22,20,18,.28);
}
.p-btn-ink:disabled {
  background: var(--p-rule);
  color: var(--p-muted);
  transform: none;
  box-shadow: none;
  cursor: not-allowed;
}
.p-btn-ghost {
  background: transparent;
  color: var(--p-ink-3);
  border: 1px solid var(--p-rule);
}
.p-btn-ghost:hover { border-color: var(--p-ink-3); color: var(--p-ink); background: var(--p-warm); }

.p-search {
  width: 100%; padding: 10px 14px 10px 40px;
  border-radius: 100px;
  border: 1px solid var(--p-rule);
  background: var(--p-surface);
  color: var(--p-ink);
  font-family: var(--p-outfit);
  font-size: .86rem;
  outline: none;
  box-sizing: border-box;
  transition: border-color .18s, box-shadow .18s;
  box-shadow: var(--p-sh-xs);
}
.p-search:focus { border-color: var(--p-ink-3); box-shadow: 0 0 0 3px rgba(22,20,18,.05); }

.p-select {
  padding: 10px 34px 10px 16px;
  border-radius: 100px;
  border: 1px solid var(--p-rule);
  background: var(--p-surface);
  color: var(--p-ink);
  font-family: var(--p-outfit);
  font-size: .83rem;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  box-shadow: var(--p-sh-xs);
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='7' viewBox='0 0 11 7'%3E%3Cpath d='M1 1l4.5 4.5L10 1' stroke='%23A09890' fill='none' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
}

.p-tab {
  padding: 8px 22px;
  border-radius: 100px;
  border: 1px solid var(--p-rule);
  background: transparent;
  color: var(--p-ink-3);
  font-family: var(--p-outfit);
  font-size: .83rem;
  font-weight: 600;
  cursor: pointer;
  transition: all .16s;
  letter-spacing: .01em;
}
.p-tab:hover { border-color: var(--p-ink-3); color: var(--p-ink); }
.p-tab.on { background: var(--p-ink); border-color: var(--p-ink); color: #FDFCF9; box-shadow: 0 2px 10px rgba(22,20,18,.2); }

.p-pill {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 14px;
  border-radius: 100px;
  border: 1px solid var(--p-rule);
  background: transparent;
  color: var(--p-ink-3);
  font-family: var(--p-outfit);
  font-size: .76rem;
  font-weight: 500;
  cursor: pointer;
  transition: all .15s;
  white-space: nowrap;
}
.p-pill:hover { border-color: var(--p-ink-3); color: var(--p-ink); }
.p-pill.on {
  background: var(--pill-c, var(--p-ink));
  border-color: var(--pill-c, var(--p-ink));
  color: #fff;
  font-weight: 700;
  box-shadow: 0 2px 10px rgba(0,0,0,.14);
}

.p-follow {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 16px;
  border-radius: 100px;
  font-family: var(--p-outfit);
  font-size: .77rem;
  font-weight: 700;
  cursor: pointer;
  transition: all .16s;
  letter-spacing: .01em;
}

.p-react {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 20px;
  border-radius: 100px;
  border: 1px solid var(--p-rule);
  background: var(--p-surface);
  color: var(--p-ink-3);
  font-family: var(--p-outfit);
  font-weight: 600;
  font-size: .86rem;
  cursor: pointer;
  transition: all .18s;
}
.p-react:hover { border-color: var(--p-ink-3); color: var(--p-ink); }
.p-react.liked    { border-color: var(--react-c); background: var(--react-l); color: var(--react-c); }
.p-react.disliked { border-color: #B8421E; background: #FAE9E4; color: #B8421E; }
.p-react.heart-pulse { animation: pHeartBeat .45s ease; }

.p-card-react {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 13px;
  border-radius: 100px;
  border: 1px solid var(--p-rule-s);
  background: var(--p-warm);
  font-family: var(--p-outfit);
  font-weight: 700;
  font-size: .78rem;
  cursor: pointer;
  color: var(--p-ink-3);
  transition: all .15s;
}
.p-card-react.liked    { border-color: var(--react-c); background: var(--react-l); color: var(--react-c); }
.p-card-react.disliked { border-color: #B8421E; background: #FAE9E4; color: #B8421E; }

.p-editor-content[data-placeholder]:empty::before {
  content: attr(data-placeholder);
  color: var(--p-muted);
  pointer-events: none;
  font-style: italic;
}
.p-editor-content blockquote {
  border-left: 2px solid var(--p-rule);
  padding-left: 20px;
  margin: 16px 0;
  font-style: italic;
  color: var(--p-ink-3);
}

.p-input {
  width: 100%; padding: 13px 16px;
  border-radius: var(--p-r-sm);
  border: 1px solid var(--p-rule);
  background: var(--p-surface);
  color: var(--p-ink);
  font-family: var(--p-outfit);
  font-size: .95rem;
  outline: none;
  box-sizing: border-box;
  transition: border-color .18s;
}
.p-input:focus { border-color: var(--p-ink-3); }

.p-notice {
  padding: 12px 18px;
  border-radius: var(--p-r-sm);
  font-family: var(--p-outfit);
  font-size: .85rem;
  font-weight: 500;
  border: 1px solid;
}
.p-notice.err  { background: #FEF2F2; color: #991B1B; border-color: #FECACA; }
.p-notice.ok   { background: #EAF3EE; color: #1E5E38; border-color: #A7D7B7; }

.p-scroll-top {
  position: fixed; bottom: 28px; right: 28px; z-index: 490;
  width: 44px; height: 44px; border-radius: 50%;
  background: var(--p-ink); color: var(--p-bg);
  border: none; cursor: pointer;
  font-family: var(--p-outfit); font-size: .9rem;
  box-shadow: 0 4px 20px rgba(22,20,18,.3);
  display: flex; align-items: center; justify-content: center;
  transition: transform .2s, box-shadow .2s;
}
.p-scroll-top:hover { transform: translateY(-4px); box-shadow: 0 8px 28px rgba(22,20,18,.38); }

.p-read-progress {
  height: 2px;
  background: var(--prog-c, var(--p-accent));
  transition: width .12s linear;
  border-radius: 0 2px 2px 0;
}

/* ─── Mobile breakpoints ─────────────────────────────────────────────────────
   FIX: Most layout was hardcoded in inline styles. These rules override the
   key pain points at ≤640 px (phones in portrait).
*/
@media (max-width: 640px) {
  /* Modal: full-screen on mobile, minimal padding */
  .p-modal-wrap {
    padding: 0 !important;
    align-items: flex-end !important;
  }
  .p-modal {
    border-radius: 18px 18px 0 0 !important;
    max-height: 96vh !important;
  }
  /* Reading column: tight padding */
  .p-reading-col {
    padding: 0 18px 56px !important;
  }
  /* Art banner: shorter on mobile */
  .p-modal-art {
    height: 110px !important;
  }
  /* Featured card: single column */
  .p-featured-card {
    grid-template-columns: 1fr !important;
  }
  /* Toolbar wraps gracefully */
  .p-editor-toolbar {
    flex-wrap: wrap;
    gap: 4px !important;
  }
  /* Main feed header: stack */
  .p-feed-header {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 12px !important;
  }
  /* Theme pills: scrollable row */
  .p-pill-row {
    flex-wrap: nowrap !important;
    overflow-x: auto;
    padding-bottom: 6px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .p-pill-row::-webkit-scrollbar { display: none; }
  /* Search row: stack */
  .p-search-row {
    flex-direction: column !important;
    align-items: stretch !important;
  }
  .p-search-row .p-select { width: 100%; }
  /* Reduce heading sizes */
  .p-t-display { font-size: clamp(1.5rem,6vw,2.2rem) !important; }
  /* Prose font smaller */
  .p-prose { font-size: 1.08rem !important; line-height: 1.85 !important; }
  /* Pull quote smaller */
  .p-pull-quote { font-size: 1.3rem !important; padding: 24px 12px !important; }
  /* Reaction buttons: smaller */
  .p-react { padding: 8px 14px !important; font-size: .82rem !important; }
  /* Scroll-to-top: closer to edge */
  .p-scroll-top { bottom: 20px; right: 16px; }
}
`;

function injectStyles(){
  if(document.getElementById("p-global-css"))return;
  const s=document.createElement("style"); s.id="p-global-css"; s.textContent=GLOBAL_CSS;
  document.head.appendChild(s);
  ["https://fonts.googleapis.com","https://fonts.gstatic.com"].forEach((href,i)=>{
    const l=document.createElement("link"); l.rel="preconnect"; l.href=href;
    if(i===1)l.crossOrigin="";
    document.head.appendChild(l);
  });
}

/* ══════════════════════════════════════════════
   FLOATING TOOLBAR BUTTON
══════════════════════════════════════════════ */
function ToolBtn({title,active,onClick,children}){
  const [h,sH]=useState(false);
  return(
    <button title={title}
      onMouseDown={e=>{e.preventDefault();onClick();}}
      onMouseEnter={()=>sH(true)} onMouseLeave={()=>sH(false)}
      style={{display:"inline-flex",alignItems:"center",justifyContent:"center",
        width:30,height:30,borderRadius:6,border:"none",flexShrink:0,
        background:active?"rgba(255,255,255,.22)":h?"rgba(255,255,255,.12)":"transparent",
        color:active?"#fff":h?"rgba(255,255,255,.85)":"rgba(255,255,255,.5)",
        cursor:"pointer",fontFamily:"var(--p-outfit)",transition:"all .12s"}}>
      {children}
    </button>
  );
}

/* ══════════════════════════════════════════════
   SKELETON CARD
══════════════════════════════════════════════ */
function SkeletonCard(){
  return(
    <div style={{background:"var(--p-surface)",border:"1px solid var(--p-rule)",
      borderRadius:"var(--p-r-lg)",overflow:"hidden",boxShadow:"var(--p-sh-sm)"}}>
      <div className="p-skel" style={{height:176,borderRadius:0}}/>
      <div style={{padding:"18px 20px 22px",display:"flex",flexDirection:"column",gap:12}}>
        <div className="p-skel" style={{width:88,height:18,borderRadius:100}}/>
        <div style={{display:"flex",flexDirection:"column",gap:7}}>
          <div className="p-skel" style={{width:"90%",height:21}}/>
          <div className="p-skel" style={{width:"65%",height:21}}/>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:5}}>
          {[100,95,86,70].map((w,i)=><div key={i} className="p-skel" style={{width:`${w}%`,height:12}}/>)}
        </div>
        <div style={{display:"flex",gap:7,marginTop:3}}>
          <div className="p-skel" style={{width:72,height:32,borderRadius:100}}/>
          <div className="p-skel" style={{width:64,height:32,borderRadius:100}}/>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,paddingTop:14,borderTop:"1px solid var(--p-rule-s)"}}>
          <div className="p-skel" style={{width:32,height:32,borderRadius:"50%",flexShrink:0}}/>
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            <div className="p-skel" style={{width:96,height:11}}/>
            <div className="p-skel" style={{width:68,height:10}}/>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   RICH TEXT EDITOR
══════════════════════════════════════════════ */
function RichEditor({onChange,theme,initialContent=""}){
  const edRef=useRef(null); const cRef=useRef(null);
  const [fmt,sFmt]=useState({bold:false,italic:false,underline:false});
  const [sz,sSz]=useState("16px");
  const [clr,sClr]=useState("inherit");
  const [showC,setShowC]=useState(false);

  useEffect(()=>{
    if(edRef.current&&initialContent){edRef.current.innerHTML=sanitize(initialContent);onChange(edRef.current.innerHTML);}
  // eslint-disable-next-line
  },[]);

  const syncFmt=useCallback(()=>{sFmt({bold:document.queryCommandState("bold"),italic:document.queryCommandState("italic"),underline:document.queryCommandState("underline")});},[]);
  useEffect(()=>{document.addEventListener("selectionchange",syncFmt);return()=>document.removeEventListener("selectionchange",syncFmt);},[syncFmt]);
  useEffect(()=>{const close=e=>{if(cRef.current&&!cRef.current.contains(e.target))setShowC(false)};document.addEventListener("mousedown",close);return()=>document.removeEventListener("mousedown",close);},[]);

  function exec(cmd,val=null){edRef.current?.focus();document.execCommand(cmd,false,val);onChange(edRef.current?.innerHTML||"");syncFmt();}
  function handleInput(){onChange(edRef.current?.innerHTML||"");syncFmt();}

  function applyFontSize(px){
    const ed=edRef.current; if(!ed)return; ed.focus();
    const sel=window.getSelection();
    if(!sel||!sel.rangeCount||sel.getRangeAt(0).collapsed){
      const r=sel&&sel.rangeCount?sel.getRangeAt(0):null;
      if(r){const m=document.createElement("span");m.style.fontSize=px;m.appendChild(document.createTextNode("\u200B"));r.insertNode(m);r.setStart(m.firstChild,1);r.collapse(true);sel.removeAllRanges();sel.addRange(r);}
      sSz(px);onChange(ed.innerHTML||"");return;
    }
    const r=sel.getRangeAt(0);
    const w=document.createElement("span");w.style.fontSize=px;w.appendChild(r.extractContents());r.insertNode(w);
    ed.querySelectorAll("span:empty").forEach(el=>{if(el!==w)el.remove();});
    sel.removeAllRanges();const nr=document.createRange();nr.selectNodeContents(w);nr.collapse(false);sel.addRange(nr);
    sSz(px);onChange(ed.innerHTML||"");
  }
  function handlePaste(e){e.preventDefault();document.execCommand("insertText",false,e.clipboardData.getData("text/plain"));onChange(edRef.current?.innerHTML||"");}
  function applyColor(c){exec("foreColor",c==="inherit"?"#161412":c);sClr(c);setShowC(false);}

  const html=edRef.current?.innerHTML||"";
  const wc=countWords(html);
  const isOver=wc>1000;
  const pct=Math.min((wc/1000)*100,100);
  const barC=wc>900?"#991B1B":wc>700?"#B07A10":(theme?.color||"#3D6B57");

  return(
    <div style={{borderRadius:"var(--p-r-md)",border:`1px solid ${isOver?"#FECACA":"var(--p-rule)"}`,
      background:theme?.editorBg||"var(--p-bg)",overflow:"hidden",
      boxShadow:isOver?"0 0 0 3px rgba(153,27,27,.06)":"var(--p-sh-sm)",transition:"border-color .2s,background .5s"}}>

      {/* Floating dark toolbar — p-editor-toolbar class lets mobile CSS wrap it */}
      <div className="p-editor-toolbar" style={{background:"var(--p-ink)",padding:"7px 10px",display:"flex",alignItems:"center",gap:2}}>
        <div style={{display:"flex",gap:1,paddingRight:7,marginRight:4,borderRight:"1px solid rgba(255,255,255,.1)"}}>
          <ToolBtn title="Bold"      active={fmt.bold}      onClick={()=>exec("bold")}><b style={{fontSize:".82rem"}}>B</b></ToolBtn>
          <ToolBtn title="Italic"    active={fmt.italic}    onClick={()=>exec("italic")}><i style={{fontSize:".82rem"}}>I</i></ToolBtn>
          <ToolBtn title="Underline" active={fmt.underline} onClick={()=>exec("underline")}><u style={{fontSize:".82rem"}}>U</u></ToolBtn>
        </div>
        <div style={{display:"flex",gap:1,paddingRight:7,marginRight:4,borderRight:"1px solid rgba(255,255,255,.1)"}}>
          {FONT_SIZES.map(f=>(
            <ToolBtn key={f.value} title={f.label} active={sz===f.value} onClick={()=>applyFontSize(f.value)}>
              <span style={{fontSize:f.value==="13px"?8:f.value==="16px"?10:f.value==="20px"?13:16,fontWeight:700}}>{f.label}</span>
            </ToolBtn>
          ))}
        </div>
        <div style={{display:"flex",gap:1,paddingRight:7,marginRight:4,borderRight:"1px solid rgba(255,255,255,.1)"}}>
          <ToolBtn title="Left"   onClick={()=>exec("justifyLeft")}>  <span style={{fontSize:".72rem"}}>⬅</span></ToolBtn>
          <ToolBtn title="Center" onClick={()=>exec("justifyCenter")}><span style={{fontSize:".72rem"}}>↔</span></ToolBtn>
          <ToolBtn title="Right"  onClick={()=>exec("justifyRight")}> <span style={{fontSize:".72rem"}}>➡</span></ToolBtn>
        </div>
        <div style={{display:"flex",gap:1,paddingRight:7,marginRight:4,borderRight:"1px solid rgba(255,255,255,.1)"}}>
          <ToolBtn title="Bullets"   onClick={()=>exec("insertUnorderedList")}><span style={{fontSize:".82rem"}}>•</span></ToolBtn>
          <ToolBtn title="Numbers"   onClick={()=>exec("insertOrderedList")}>  <span style={{fontSize:".72rem",fontWeight:700}}>1.</span></ToolBtn>
          <ToolBtn title="Quote"     onClick={()=>exec("formatBlock","blockquote")}><span style={{fontSize:".82rem"}}>"</span></ToolBtn>
          <ToolBtn title="Clear"     onClick={()=>exec("removeFormat")}><span style={{fontSize:".78rem"}}>✕</span></ToolBtn>
        </div>
        <div ref={cRef} style={{position:"relative"}}>
          <button title="Color" onMouseDown={e=>{e.preventDefault();setShowC(s=>!s);}}
            style={{display:"inline-flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
              width:30,height:30,borderRadius:6,gap:2,border:"none",
              background:showC?"rgba(255,255,255,.18)":"transparent",cursor:"pointer",padding:0,transition:"background .12s"}}>
            <span style={{fontSize:".8rem",fontWeight:800,color:clr==="inherit"?"#fff":clr,lineHeight:1}}>A</span>
            <span style={{width:13,height:2,borderRadius:2,background:clr==="inherit"?"rgba(255,255,255,.5)":clr}}/>
          </button>
          {showC&&(
            <div style={{position:"absolute",top:38,left:0,zIndex:300,
              background:"var(--p-surface)",border:"1px solid var(--p-rule)",
              borderRadius:"var(--p-r-md)",padding:8,boxShadow:"var(--p-sh-lg)",
              display:"flex",flexDirection:"column",gap:2,minWidth:148}}>
              {TEXT_COLORS.map(c=>(
                <button key={c.value} onMouseDown={e=>{e.preventDefault();applyColor(c.value);}}
                  style={{display:"flex",alignItems:"center",gap:9,padding:"6px 10px",borderRadius:7,cursor:"pointer",
                    border:clr===c.value?`1px solid ${c.value==="inherit"?"var(--p-ink)":c.value}`:"1px solid transparent",
                    background:clr===c.value?"var(--p-warm)":"transparent",fontFamily:"var(--p-outfit)"}}>
                  <span style={{width:14,height:14,borderRadius:"50%",flexShrink:0,background:c.value==="inherit"?"#161412":c.value,border:"1px solid var(--p-rule-s)"}}/>
                  <span style={{fontSize:".79rem",color:"var(--p-ink)",fontWeight:500}}>{c.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <span style={{marginLeft:"auto",fontFamily:"var(--p-outfit)",fontSize:".72rem",fontWeight:700,
          color:isOver?"#FC8181":wc>700?"#FBC06B":"rgba(255,255,255,.38)",letterSpacing:".02em"}}>
          {wc}/1000
        </span>
      </div>

      <div ref={edRef} contentEditable suppressContentEditableWarning
        onInput={handleInput} onPaste={handlePaste} onKeyUp={syncFmt} onMouseUp={syncFmt}
        className="p-editor-content"
        data-placeholder="Begin writing here — your story deserves to be told."
        style={{minHeight:340,padding:"28px 32px",outline:"none",
          fontFamily:"var(--p-cormorant)",fontSize:"1.12rem",lineHeight:1.9,
          color:"var(--p-ink)",caretColor:theme?.color||"var(--p-accent)",
          wordBreak:"break-word",background:"transparent"}}/>

      <div style={{height:3,background:"var(--p-rule-s)"}}>
        <div style={{height:"100%",width:`${pct}%`,background:barC,borderRadius:"0 2px 2px 0",transition:"width .3s,background .4s"}}/>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   ALL HOOKS — LOGIC COMPLETELY PRESERVED
══════════════════════════════════════════════ */
function useReactions(storyId,enabled=false,initialLikes=0,initialDislikes=0){
  const [likes,setLikes]=useState(initialLikes);
  const [dislikes,setDislikes]=useState(initialDislikes);
  const [myVote,setMyVote]=useState(null);
  const [loading,setLoading]=useState(false);
  const uid=auth.currentUser?.uid;

  useEffect(()=>{if(!enabled){setLikes(initialLikes);setDislikes(initialDislikes);}},[initialLikes,initialDislikes,enabled]);
  useEffect(()=>{
    if(!enabled||!storyId)return;
    let unsub=null,cancelled=false;
    const t=setTimeout(()=>{
      if(cancelled)return;
      unsub=onSnapshot(doc(db,"stories",storyId),{includeMetadataChanges:false},
        snap=>{if(cancelled)return;if(snap.exists()){setLikes(snap.data().likes||0);setDislikes(snap.data().dislikes||0);}},
        err=>{if(err.message?.includes("INTERNAL ASSERTION FAILED"))return;console.error(err);}
      );
    },50);
    return()=>{cancelled=true;clearTimeout(t);unsub?.();};
  },[enabled,storyId]);
  useEffect(()=>{
    if(!uid||!storyId)return;
    getDoc(doc(db,"storyReactions",storyId,"votes",uid)).then(s=>{if(s.exists())setMyVote(s.data().vote);}).catch(()=>{});
  },[storyId,uid]);

  async function vote(type){
    if(!uid||loading)return; setLoading(true);
    const pv=myVote,pl=likes,pd=dislikes;
    const vRef=doc(db,"storyReactions",storyId,"votes",uid),sRef=doc(db,"stories",storyId);
    try{
      if(pv===type){
        setMyVote(null);
        if(!enabled){if(type==="like")setLikes(l=>Math.max(0,l-1));if(type==="dislike")setDislikes(d=>Math.max(0,d-1));}
        await deleteDoc(vRef);await updateDoc(sRef,{[type==="like"?"likes":"dislikes"]:increment(-1)});
      }else{
        setMyVote(type);
        if(!enabled){if(pv==="like")setLikes(l=>Math.max(0,l-1));if(pv==="dislike")setDislikes(d=>Math.max(0,d-1));if(type==="like")setLikes(l=>l+1);if(type==="dislike")setDislikes(d=>d+1);}
        if(pv)await updateDoc(sRef,{[pv==="like"?"likes":"dislikes"]:increment(-1)});
        await setDoc(vRef,{vote:type});await updateDoc(sRef,{[type==="like"?"likes":"dislikes"]:increment(1)});
      }
    }catch(e){console.error(e);setMyVote(pv);setLikes(pl);setDislikes(pd);}
    finally{setLoading(false);}
  }
  return{likes,dislikes,myVote,vote,loggedIn:!!uid};
}

function useFollow(authorId){
  const [following,setFollowing]=useState(false);
  const [fCount,setFCount]=useState(0);
  const [loading,setLoading]=useState(false);
  const uid=auth.currentUser?.uid;
  const isSelf=!!(uid&&authorId&&uid===authorId);

  useEffect(()=>{
    if(!authorId)return;
    let unsub=null,cancelled=false;
    const t=setTimeout(()=>{
      if(cancelled)return;
      unsub=onSnapshot(doc(db,"users",authorId),{includeMetadataChanges:false},
        snap=>{if(cancelled)return;if(snap.exists())setFCount(snap.data().followerCount||0);},
        err=>{if(err.message?.includes("INTERNAL ASSERTION FAILED"))return;console.error(err);}
      );
    },50);
    return()=>{cancelled=true;clearTimeout(t);unsub?.();};
  },[authorId]);
  useEffect(()=>{
    if(!uid||!authorId||isSelf)return;
    setFollowing(false);
    getDoc(doc(db,"follows",`${uid}_${authorId}`)).then(s=>setFollowing(s.exists())).catch(()=>{});
  },[uid,authorId,isSelf]);

  function syncLocal(isF){
    try{const a=JSON.parse(localStorage.getItem("hh_following")||"[]");const s=new Set(a);isF?s.add(authorId):s.delete(authorId);localStorage.setItem("hh_following",JSON.stringify([...s]));}catch{}
  }
  async function toggleFollow(){
    if(!uid){alert("Please log in to follow authors.");return;}
    if(isSelf||loading)return;
    setLoading(true);
    const was=following;setFollowing(!was);setFCount(c=>was?Math.max(0,c-1):c+1);
    const fRef=doc(db,"follows",`${uid}_${authorId}`),aRef=doc(db,"users",authorId);
    try{
      if(was){await deleteDoc(fRef);await updateDoc(aRef,{followerCount:increment(-1)});syncLocal(false);}
      else{await setDoc(fRef,{followerId:uid,authorId,createdAt:serverTimestamp()});await updateDoc(aRef,{followerCount:increment(1)});syncLocal(true);}
    }catch(e){console.error(e);setFollowing(was);setFCount(c=>was?c+1:Math.max(0,c-1));}
    finally{setLoading(false);}
  }
  return{following,fCount,toggleFollow,isSelf,loggedIn:!!uid};
}

function useBookmarks(){
  const [bookmarks,setBookmarks]=useState(()=>{try{return new Set(JSON.parse(localStorage.getItem("hh_bookmarks")||"[]"));}catch{return new Set();}});
  function toggle(id){
    setBookmarks(prev=>{const n=new Set(prev);n.has(id)?n.delete(id):n.add(id);try{localStorage.setItem("hh_bookmarks",JSON.stringify([...n]));}catch{}return n;});
  }
  return{bookmarks,toggle};
}

/* ══════════════════════════════════════════════
   COMMENTS SECTION
══════════════════════════════════════════════ */
function CommentsSection({storyId}){
  const [comments,setComments]=useState([]);
  const [text,setText]=useState("");
  const [loading,setLoading]=useState(true);
  const [posting,setPosting]=useState(false);
  const [displayName,setDisplayName]=useState("Anonymous");
  const uid=auth.currentUser?.uid;

  useEffect(()=>{
    if(!uid)return;
    getDoc(doc(db,"users",uid)).then(s=>{if(s.exists()&&s.data().name)setDisplayName(s.data().name);else if(auth.currentUser?.displayName)setDisplayName(auth.currentUser.displayName);}).catch(()=>{});
  },[uid]);

  useEffect(()=>{
    if(!storyId)return;
    let unsub=null,cancelled=false;setLoading(true);
    const t=setTimeout(()=>{
      if(cancelled)return;
      const q=query(collection(db,"stories",storyId,"comments"),orderBy("createdAt","desc"),limit(30));
      unsub=onSnapshot(q,{includeMetadataChanges:false},
        snap=>{if(cancelled)return;setComments(snap.docs.map(d=>({id:d.id,...d.data()})));setLoading(false);},
        err=>{if(err.message?.includes("INTERNAL ASSERTION FAILED"))return;console.error(err);setLoading(false);}
      );
    },50);
    return()=>{cancelled=true;clearTimeout(t);unsub?.();};
  },[storyId]);

  async function postComment(){
    if(!uid){alert("Please log in to comment.");return;}
    if(!text.trim()||posting)return;
    const fl=containsBannedWords(text);if(fl.length){alert(`Flagged: "${fl[0]}"`);return;}
    setPosting(true);
    try{
      await addDoc(collection(db,"stories",storyId,"comments"),{text:text.trim(),authorName:displayName,authorId:uid,createdAt:serverTimestamp()});
      await updateDoc(doc(db,"stories",storyId),{commentCount:increment(1)});
      setText("");
    }catch(e){console.error(e);}
    finally{setPosting(false);}
  }
  async function deleteComment(cid,cAuthorId){
    if(!uid||uid!==cAuthorId)return;
    if(!window.confirm("Delete this comment?"))return;
    try{await deleteDoc(doc(db,"stories",storyId,"comments",cid));await updateDoc(doc(db,"stories",storyId),{commentCount:increment(-1)});}
    catch(e){console.error(e);}
  }

  return(
    <div style={{marginTop:44,paddingTop:36,borderTop:"1px solid var(--p-rule)"}}>
      <h4 style={{margin:"0 0 22px",fontFamily:"var(--p-cormorant)",fontSize:"1.4rem",fontWeight:700,color:"var(--p-ink)",display:"flex",alignItems:"center",gap:10,letterSpacing:"-.01em"}}>
        Responses
        {!loading&&comments.length>0&&<span style={{fontFamily:"var(--p-outfit)",fontSize:".72rem",fontWeight:500,color:"var(--p-muted)"}}>({comments.length})</span>}
      </h4>

      {uid?(
        <div style={{display:"flex",gap:12,marginBottom:28,alignItems:"flex-start"}}>
          <div style={{width:36,height:36,borderRadius:"50%",background:"var(--p-ink)",color:"var(--p-bg)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"var(--p-cormorant)",fontWeight:700,fontSize:".95rem",flexShrink:0,marginTop:2}}>
            {displayName[0].toUpperCase()}
          </div>
          <div style={{flex:1}}>
            <textarea className="p-comment-input" rows={2} value={text}
              onChange={e=>setText(e.target.value)}
              placeholder="Share your thoughts…"
              onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();postComment();}}}/>
            <div style={{display:"flex",justifyContent:"flex-end",marginTop:8}}>
              <button onClick={postComment} disabled={!text.trim()||posting}
                className="p-btn p-btn-ink" style={{padding:"8px 20px",fontSize:".82rem"}}>
                {posting?"Publishing…":"Respond"}
              </button>
            </div>
          </div>
        </div>
      ):(
        <p style={{fontFamily:"var(--p-outfit)",fontSize:".85rem",color:"var(--p-muted)",marginBottom:20,fontStyle:"italic"}}>Sign in to leave a response.</p>
      )}

      {loading?(
        <div style={{display:"flex",flexDirection:"column",gap:20}}>
          {[1,2,3].map(i=>(
            <div key={i} style={{display:"flex",gap:12}}>
              <div className="p-skel" style={{width:34,height:34,borderRadius:"50%",flexShrink:0}}/>
              <div style={{flex:1,display:"flex",flexDirection:"column",gap:5}}>
                <div className="p-skel" style={{width:110,height:11}}/>
                <div className="p-skel" style={{width:"82%",height:11}}/>
                <div className="p-skel" style={{width:"60%",height:11}}/>
              </div>
            </div>
          ))}
        </div>
      ):comments.length===0?(
        <p style={{fontFamily:"var(--p-outfit)",fontSize:".88rem",color:"var(--p-muted)",textAlign:"center",padding:"28px 0",fontStyle:"italic"}}>
          No responses yet. Start the conversation.
        </p>
      ):(
        <div style={{display:"flex",flexDirection:"column"}}>
          {comments.map((c,idx)=>(
            <div key={c.id} style={{display:"flex",gap:14,alignItems:"flex-start",
              padding:"18px 0",borderBottom:idx<comments.length-1?"1px solid var(--p-rule-s)":"none"}}>
              <div style={{width:34,height:34,borderRadius:"50%",background:"var(--p-warm)",border:"1px solid var(--p-rule)",color:"var(--p-ink-3)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"var(--p-cormorant)",fontWeight:700,fontSize:".9rem",flexShrink:0}}>
                {(c.authorName||"A")[0].toUpperCase()}
              </div>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                  <span style={{fontFamily:"var(--p-outfit)",fontSize:".83rem",fontWeight:700,color:"var(--p-ink)"}}>{c.authorName||"Anonymous"}</span>
                  <span style={{fontFamily:"var(--p-outfit)",fontSize:".7rem",color:"var(--p-muted)"}}>
                    {c.createdAt?.toDate?.().toLocaleDateString("en-IN",{day:"numeric",month:"short"})}
                  </span>
                  {uid&&c.authorId===uid&&(
                    <button onClick={()=>deleteComment(c.id,c.authorId)} title="Delete"
                      style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",fontFamily:"var(--p-outfit)",fontSize:".74rem",color:"var(--p-muted)",opacity:.6,padding:0,transition:"opacity .15s"}}
                      onMouseEnter={e=>e.currentTarget.style.opacity="1"}
                      onMouseLeave={e=>e.currentTarget.style.opacity=".6"}>
                      Delete
                    </button>
                  )}
                </div>
                <p style={{margin:0,fontFamily:"var(--p-outfit)",fontSize:".9rem",color:"var(--p-ink-2)",lineHeight:1.7}}>{c.text}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   READING MODAL
   FIXES:
   - Added className="p-modal-wrap" to overlay so mobile CSS can make it
     bottom-sheet style (align-items:flex-end, full width, rounded top).
   - Added className="p-modal-art" to art banner so mobile CSS can reduce
     its height.
   - Added className="p-reading-col" to reading column so mobile CSS can
     reduce horizontal padding from 36px to 18px.
   - Removed hardcoded padding:"0 36px 64px" from reading column (now via CSS).
══════════════════════════════════════════════ */
function StoryModal({story,onClose,isAdmin,onDelete}){
  const [scrollPct,setScrollPct]=useState(0);
  const bodyRef=useRef(null);
  const isMobile=useIsMobile();
  const theme=STORY_THEMES.find(t=>t.id===story.theme)||STORY_THEMES[0];
  const uid=auth.currentUser?.uid;
  const words=countWords(story.content);
  const readingTime=Math.max(1,Math.ceil(words/200));
  const sanitizedContent=useMemo(()=>sanitize(story.content),[story.content]);
  const [likeAnim,setLikeAnim]=useState(false);

  const pullQuote=useMemo(()=>{
    const plain=story.content.replace(/<[^>]*>/g," ").replace(/\s+/g," ").trim();
    const sentences=plain.split(/[.!?]+/).filter(s=>s.trim().length>40&&s.trim().length<160);
    if(!sentences.length)return null;
    const mid=Math.floor(sentences.length/2);
    return sentences[Math.min(mid,sentences.length-1)].trim();
  },[story.content]);

  const {likes,dislikes,myVote,vote,loggedIn:likeLoggedIn}=useReactions(story.id,true,story.likes||0,story.dislikes||0);
  const {following,fCount,toggleFollow,isSelf,loggedIn:followLoggedIn}=useFollow(story.authorId);

  useEffect(()=>{
    const el=bodyRef.current;if(!el)return;
    const fn=()=>{const{scrollTop,scrollHeight,clientHeight}=el;setScrollPct(scrollHeight<=clientHeight?100:Math.round((scrollTop/(scrollHeight-clientHeight))*100));};
    el.addEventListener("scroll",fn);return()=>el.removeEventListener("scroll",fn);
  },[]);

  useEffect(()=>{
    const scrollY=window.scrollY;
    document.body.style.overflow="hidden";document.body.style.position="fixed";
    document.body.style.top=`-${scrollY}px`;document.body.style.width="100%";
    return()=>{document.body.style.overflow="";document.body.style.position="";document.body.style.top="";document.body.style.width="";window.scrollTo(0,scrollY);};
  },[]);

  useEffect(()=>{
    const fn=e=>{if(e.key==="Escape")onClose();};
    document.addEventListener("keydown",fn);return()=>document.removeEventListener("keydown",fn);
  },[onClose]);

  function handleVote(type){
    if(!likeLoggedIn){alert("Please log in to vote.");return;}
    if(type==="like"){setLikeAnim(true);setTimeout(()=>setLikeAnim(false),500);}
    vote(type);
  }

  async function handleReport(){
    if(!uid){alert("Log in to report.");return;}
    if(!window.confirm("Report this story for inappropriate content?"))return;
    try{await addDoc(collection(db,"reports"),{storyId:story.id,reporterId:uid,timestamp:serverTimestamp(),status:"pending"});alert("Story reported. Thank you!");}
    catch(e){console.error(e);}
  }

  async function handleDelete(){
    if(!window.confirm("Delete this story permanently?"))return;
    try{await deleteDoc(doc(db,"stories",story.id));onDelete(story.id);onClose();}
    catch(e){console.error(e);}
  }

  const pubDate=story.createdAt?.toDate
    ?story.createdAt.toDate().toLocaleDateString("en-IN",{day:"numeric",month:"long",year:"numeric"}):"";

  return createPortal(
    /* className="p-modal-wrap" — mobile CSS converts to bottom-sheet */
    <div className="p-overlay p-modal-wrap" onClick={onClose}
      style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(10,8,5,.75)",
        backdropFilter:"blur(10px)",display:"flex",alignItems:"center",justifyContent:"center",
        padding:isMobile?0:16}}>

      <div className="p-modal" onClick={e=>e.stopPropagation()}
        style={{width:"100%",maxWidth:820,
          maxHeight:isMobile?"96vh":"94vh",
          borderRadius:isMobile?"18px 18px 0 0":"var(--p-r-lg)",
          background:"var(--p-surface)",boxShadow:"var(--p-sh-xl)",
          display:"flex",flexDirection:"column",overflow:"hidden"}}>

        {/* Progress bar */}
        <div style={{height:2,background:"var(--p-rule-s)",flexShrink:0}}>
          <div className="p-read-progress" style={{width:`${scrollPct}%`,background:theme.color}}/>
        </div>

        {/* Modal toolbar */}
        <div style={{padding:isMobile?"10px 14px":"11px 20px",borderBottom:"1px solid var(--p-rule-s)",
          display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,gap:8}}>
          <div style={{display:"flex",alignItems:"center",gap:10,overflow:"hidden"}}>
            <span style={{fontFamily:"var(--p-outfit)",fontSize:".62rem",fontWeight:700,
              letterSpacing:".1em",textTransform:"uppercase",color:theme.color,whiteSpace:"nowrap"}}>
              {theme.emoji} {theme.label}
            </span>
            <span style={{width:3,height:3,borderRadius:"50%",background:"var(--p-rule)",display:"block",flexShrink:0}}/>
            <span style={{fontFamily:"var(--p-outfit)",fontSize:".72rem",color:"var(--p-muted)",whiteSpace:"nowrap"}}>
              {words.toLocaleString()} words · {readingTime} min
            </span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
            {(isSelf||isAdmin)&&(
              <button onClick={handleDelete}
                style={{background:"none",border:"none",cursor:"pointer",padding:"5px 10px",borderRadius:7,
                  fontFamily:"var(--p-outfit)",fontSize:".78rem",color:"var(--p-muted)",transition:"all .15s"}}
                onMouseEnter={e=>{e.currentTarget.style.background="#FEF2F2";e.currentTarget.style.color="#991B1B";}}
                onMouseLeave={e=>{e.currentTarget.style.background="none";e.currentTarget.style.color="var(--p-muted)";}}>
                Delete
              </button>
            )}
            {!isSelf&&uid&&(
              <button onClick={handleReport}
                style={{background:"none",border:"none",cursor:"pointer",padding:"5px 10px",borderRadius:7,
                  fontFamily:"var(--p-outfit)",fontSize:".78rem",color:"var(--p-muted)",opacity:.7,transition:"opacity .15s"}}>
                Report
              </button>
            )}
            <button onClick={onClose}
              style={{width:30,height:30,borderRadius:"50%",border:"1px solid var(--p-rule)",
                background:"none",cursor:"pointer",display:"flex",alignItems:"center",
                justifyContent:"center",color:"var(--p-ink-3)",fontSize:".88rem",transition:"all .15s"}}
              onMouseEnter={e=>e.currentTarget.style.background="var(--p-warm)"}
              onMouseLeave={e=>e.currentTarget.style.background="none"}>
              ✕
            </button>
          </div>
        </div>

        {/* Reading body */}
        <div ref={bodyRef} style={{overflowY:"auto",flex:1,overscrollBehavior:"contain"}}>

          {/* Abstract art banner — className allows mobile CSS to shrink it */}
          <div className="p-modal-art" style={{height:isMobile?110:160,flexShrink:0,position:"relative",overflow:"hidden"}}>
            <ThemeArt theme={theme} height={isMobile?110:160}/>
            <div style={{position:"absolute",bottom:0,left:0,right:0,height:60,
              background:"linear-gradient(to bottom, transparent, var(--p-surface))"}}/>
          </div>

          {/* Reading column — className="p-reading-col" for mobile padding override */}
          <div className="p-reading-col"
            style={{maxWidth:720,margin:"0 auto",
              padding:isMobile?"0 18px 56px":"0 36px 64px"}}>

            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:18}}>
              <span style={{fontFamily:"var(--p-outfit)",fontSize:".62rem",fontWeight:700,
                letterSpacing:".12em",textTransform:"uppercase",color:theme.color}}>
                {theme.label}
              </span>
              {pubDate&&(
                <>
                  <span style={{color:"var(--p-rule)",fontSize:"1rem"}}>·</span>
                  <span style={{fontFamily:"var(--p-outfit)",fontSize:".72rem",color:"var(--p-muted)"}}>{pubDate}</span>
                </>
              )}
            </div>

            <h1 className="p-t-display" style={{fontSize:"clamp(1.5rem,5vw,2.5rem)",marginBottom:24,lineHeight:1.15}}>
              {story.title}
            </h1>

            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
              marginBottom:36,paddingBottom:24,borderBottom:"1px solid var(--p-rule)",flexWrap:"wrap",gap:12}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:44,height:44,borderRadius:"50%",background:theme.light,
                  border:`2px solid ${theme.color}20`,display:"flex",alignItems:"center",
                  justifyContent:"center",fontFamily:"var(--p-cormorant)",fontWeight:700,
                  fontSize:"1.15rem",color:theme.color,flexShrink:0}}>
                  {(story.authorName||"A")[0].toUpperCase()}
                </div>
                <div>
                  <div style={{fontFamily:"var(--p-outfit)",fontSize:".92rem",fontWeight:700,color:"var(--p-ink)"}}>
                    {story.authorName||"Anonymous"}
                  </div>
                  <div style={{fontFamily:"var(--p-outfit)",fontSize:".72rem",color:"var(--p-muted)",marginTop:2}}>
                    {fCount>0?`${fCount} follower${fCount!==1?"s":""}  · `:""}
                    {readingTime} min read
                  </div>
                </div>
              </div>
              {!isSelf&&story.authorId&&(
                <button
                  onClick={()=>{if(!followLoggedIn){alert("Please log in.");return;}toggleFollow();}}
                  className="p-follow"
                  style={{border:following?`1.5px solid ${theme.color}`:"1px solid var(--p-rule)",
                    background:following?theme.light:"var(--p-surface)",
                    color:following?theme.color:"var(--p-ink-3)"}}>
                  {following?"✓ Following":"+ Follow"}
                </button>
              )}
            </div>

            {/* Story prose — drop-cap is now CSS-safe */}
            <div className="p-prose" style={{"--drop-color":theme.color}}
              dangerouslySetInnerHTML={{__html:sanitizedContent}}/>

            {pullQuote&&(
              <div className="p-pull-quote" style={{color:theme.textColor||theme.color}}>
                "{pullQuote}"
              </div>
            )}

            <div className="p-ornament">— ◆ —</div>

            <div style={{display:"flex",alignItems:"center",gap:10,marginTop:4,flexWrap:"wrap"}}>
              <button
                onClick={()=>handleVote("like")}
                className={`p-react${myVote==="like"?" liked":""}${likeAnim?" heart-pulse":""}`}
                style={{"--react-c":theme.color,"--react-l":theme.light}}>
                {myVote==="like"?"❤️":"🤍"} {likes} {likes===1?"Like":"Likes"}
              </button>
              <button onClick={()=>handleVote("dislike")} className={`p-react${myVote==="dislike"?" disliked":""}`}>
                👎 {dislikes}
              </button>
              {!likeLoggedIn&&(
                <span style={{fontFamily:"var(--p-outfit)",fontSize:".75rem",color:"var(--p-muted)",fontStyle:"italic"}}>
                  Sign in to react
                </span>
              )}
            </div>

            <CommentsSection storyId={story.id}/>
          </div>
        </div>
      </div>

    </div>
  , document.body);
}

/* ══════════════════════════════════════════════
   STORY CARD
   FIX: Removed hovered state + onMouseEnter/onMouseLeave handlers.
   The blink was caused by translateY(-4px) moving the card out from under
   the cursor, firing onMouseLeave, snapping back, re-triggering onMouseEnter
   in an infinite loop.  CSS :hover (via .p-story-card class) never blinks
   because the browser batches the hover state change and the repaint together.
══════════════════════════════════════════════ */
function StoryCard({story,onDelete,isAdmin,bookmarks,onBookmarkToggle,onOpenModal,featured=false}){
  const [likeAnim,setLikeAnim]=useState(false);
  const isMobile=useIsMobile();
  const theme=STORY_THEMES.find(t=>t.id===story.theme)||STORY_THEMES[0];
  const uid=auth.currentUser?.uid;

  const words=countWords(story.content);
  const readingTime=Math.max(1,Math.ceil(words/200));
  const plain=story.content.replace(/<[^>]*>/g," ").replace(/\s+/g," ").trim();
  const isBookmarked=bookmarks.has(story.id);
  const isPopular=(story.likes||0)>=10;

  const {likes,dislikes,myVote,vote,loggedIn:likeLoggedIn}=useReactions(story.id,false,story.likes||0,story.dislikes||0);
  const {following,fCount,toggleFollow,isSelf,loggedIn:followLoggedIn}=useFollow(story.authorId);

  function handleVote(type){
    if(!likeLoggedIn){alert("Please log in to vote.");return;}
    if(type==="like"){setLikeAnim(true);setTimeout(()=>setLikeAnim(false),500);}
    vote(type);
  }

  async function handleReport(){
    if(!uid){alert("Log in to report.");return;}
    if(!window.confirm("Report this story?"))return;
    try{await addDoc(collection(db,"reports"),{storyId:story.id,reporterId:uid,timestamp:serverTimestamp(),status:"pending"});alert("Reported. Thank you!");}
    catch(e){console.error(e);}
  }

  async function handleDelete(){
    if(window.confirm("Delete this story?")){
      try{await deleteDoc(doc(db,"stories",story.id));if(onDelete)onDelete(story.id);}
      catch(e){console.error(e);}
    }
  }

  const dateStr=story.createdAt?.toDate
    ?story.createdAt.toDate().toLocaleDateString("en-IN",{day:"numeric",month:"short"}):"";
  const preview=plain.split(" ").slice(0,featured?55:38).join(" ");

  if(featured){
    return(
      /* className="p-story-card p-featured-card" — p-featured-card collapses to 1-col on mobile */
      <div className="p-card-enter p-story-card p-featured-card"
        style={{background:"var(--p-surface)",border:"1px solid var(--p-rule)",
          borderRadius:"var(--p-r-lg)",overflow:"hidden",position:"relative",
          display:"grid",
          gridTemplateColumns:isMobile?"1fr":"1.1fr 1fr",
          minHeight:isMobile?undefined:320}}>

        <div style={{position:"relative",overflow:"hidden",minHeight:isMobile?160:undefined}}>
          <ThemeArt theme={theme} height={isMobile?160:320}/>
          <div style={{position:"absolute",inset:0,background:`linear-gradient(135deg,${theme.color}08,${theme.color}18)`}}/>
          {isPopular&&(
            <div style={{position:"absolute",top:18,left:18,
              background:theme.color,color:"#fff",fontFamily:"var(--p-outfit)",
              fontSize:".6rem",fontWeight:700,padding:"4px 11px",
              borderRadius:100,letterSpacing:".06em",textTransform:"uppercase"}}>
              ★ Popular
            </div>
          )}
        </div>

        <div style={{padding:isMobile?"20px 18px 22px":"30px 28px 28px",display:"flex",flexDirection:"column",gap:14,position:"relative"}}>
          <div style={{position:"absolute",top:14,right:14,display:"flex",gap:2}}>
            <button onClick={()=>onBookmarkToggle(story.id)} title={isBookmarked?"Saved":"Save"}
              style={{background:"none",border:"none",cursor:"pointer",padding:"5px 7px",
                borderRadius:7,opacity:isBookmarked?1:.35,color:isBookmarked?theme.color:"var(--p-ink)",
                fontSize:".9rem",transition:"all .15s"}}>
              🔖
            </button>
            {(isSelf||isAdmin)&&<button onClick={handleDelete} style={{background:"none",border:"none",cursor:"pointer",padding:"5px 7px",borderRadius:7,fontSize:".85rem",opacity:.3,transition:"opacity .15s"}} onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity=".3"}>🗑️</button>}
            {!isSelf&&<button onClick={handleReport} style={{background:"none",border:"none",cursor:"pointer",padding:"5px 7px",borderRadius:7,fontSize:".85rem",opacity:.22,transition:"opacity .15s"}} onMouseEnter={e=>e.currentTarget.style.opacity=".75"} onMouseLeave={e=>e.currentTarget.style.opacity=".22"}>🚩</button>}
          </div>

          <div>
            <div style={{fontFamily:"var(--p-outfit)",fontSize:".6rem",fontWeight:700,
              letterSpacing:".12em",textTransform:"uppercase",color:theme.color,marginBottom:10}}>
              {theme.emoji} {theme.label}
            </div>
            <h2 className="p-t-heading"
              onClick={()=>onOpenModal(story)}
              style={{fontSize:isMobile?"1.15rem":"1.35rem",cursor:"pointer",marginBottom:10,transition:"color .15s",paddingRight:40}}
              onMouseEnter={e=>e.currentTarget.style.color=theme.color}
              onMouseLeave={e=>e.currentTarget.style.color="var(--p-ink)"}>
              {story.title}
            </h2>
            <p className="p-t-body" style={{fontSize:".88rem",lineHeight:1.75}}>
              {preview}…
            </p>
          </div>

          <button onClick={()=>onOpenModal(story)}
            style={{alignSelf:"flex-start",background:"none",border:"none",cursor:"pointer",
              fontFamily:"var(--p-outfit)",fontSize:".8rem",fontWeight:700,color:theme.color,
              padding:0,letterSpacing:".02em",transition:"opacity .15s"}}
            onMouseEnter={e=>e.currentTarget.style.opacity=".65"}
            onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
            Read full story →
          </button>

          <div style={{display:"flex",gap:7}}>
            <button onClick={()=>handleVote("like")} className={`p-card-react${myVote==="like"?" liked":""}${likeAnim?" heart-pulse":""}`} style={{"--react-c":theme.color,"--react-l":theme.light}}>
              {myVote==="like"?"❤️":"🤍"} <span>{likes}</span>
            </button>
            <button onClick={()=>handleVote("dislike")} className={`p-card-react${myVote==="dislike"?" disliked":""}`}>
              👎 <span>{dislikes}</span>
            </button>
          </div>

          <div style={{marginTop:"auto",paddingTop:14,borderTop:"1px solid var(--p-rule-s)",
            display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
            <div style={{display:"flex",alignItems:"center",gap:9}}>
              <div style={{width:30,height:30,borderRadius:"50%",background:theme.light,color:theme.color,
                display:"flex",alignItems:"center",justifyContent:"center",
                fontFamily:"var(--p-cormorant)",fontWeight:700,fontSize:".88rem",flexShrink:0}}>
                {(story.authorName||"A")[0].toUpperCase()}
              </div>
              <div>
                <div style={{fontFamily:"var(--p-outfit)",fontSize:".8rem",fontWeight:700,color:"var(--p-ink)"}}>
                  {story.authorName||"Anonymous"}
                </div>
                <div style={{fontFamily:"var(--p-outfit)",fontSize:".68rem",color:"var(--p-muted)"}}>
                  {readingTime} min · {dateStr}
                </div>
              </div>
            </div>
            {!isSelf&&story.authorId&&(
              <button onClick={()=>{if(!followLoggedIn){alert("Please log in.");return;}toggleFollow();}}
                className="p-follow"
                style={{border:following?`1.5px solid ${theme.color}`:"1px solid var(--p-rule)",
                  background:following?theme.light:"var(--p-surface)",
                  color:following?theme.color:"var(--p-ink-3)",
                  fontSize:".74rem",padding:"5px 14px"}}>
                {following?"✓":"+"} 
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* Standard vertical card — className="p-story-card" enables CSS-only hover */
  return(
    <div className="p-card-enter p-story-card"
      style={{background:"var(--p-surface)",border:"1px solid var(--p-rule)",
        borderRadius:"var(--p-r-lg)",overflow:"hidden",position:"relative",
        display:"flex",flexDirection:"column"}}>

      <div style={{position:"relative",overflow:"hidden"}}>
        <ThemeArt theme={theme} height={170}/>
        <div style={{position:"absolute",inset:0,background:`linear-gradient(to bottom,transparent 60%,var(--p-surface))`}}/>
        <div style={{position:"absolute",top:10,right:10,display:"flex",gap:2}}>
          <button onClick={()=>onBookmarkToggle(story.id)} title={isBookmarked?"Saved":"Save"}
            style={{background:"rgba(255,255,255,.8)",backdropFilter:"blur(6px)",
              border:"1px solid rgba(255,255,255,.5)",cursor:"pointer",padding:"4px 7px",
              borderRadius:8,opacity:isBookmarked?1:.7,color:isBookmarked?theme.color:"var(--p-ink-3)",
              fontSize:".82rem",transition:"all .15s"}}>
            🔖
          </button>
          {isPopular&&(
            <div style={{background:theme.color,color:"#fff",fontFamily:"var(--p-outfit)",
              fontSize:".58rem",fontWeight:700,padding:"4px 9px",borderRadius:100,
              letterSpacing:".06em",textTransform:"uppercase",alignSelf:"center"}}>
              ★ Popular
            </div>
          )}
        </div>
        {(isSelf||isAdmin)&&(
          <button onClick={handleDelete}
            style={{position:"absolute",top:10,left:10,background:"rgba(255,255,255,.8)",
              backdropFilter:"blur(6px)",border:"none",cursor:"pointer",padding:"4px 7px",
              borderRadius:8,fontSize:".8rem",opacity:.5,transition:"opacity .15s"}}
            onMouseEnter={e=>e.currentTarget.style.opacity="1"}
            onMouseLeave={e=>e.currentTarget.style.opacity=".5"}>
            🗑️
          </button>
        )}
      </div>

      <div style={{padding:"18px 20px 22px",display:"flex",flexDirection:"column",gap:11,flex:1}}>
        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          <span style={{fontFamily:"var(--p-outfit)",fontSize:".6rem",fontWeight:700,
            letterSpacing:".1em",textTransform:"uppercase",color:theme.color}}>
            {theme.emoji} {theme.label}
          </span>
          <span style={{color:"var(--p-rule)",fontSize:".9rem"}}>·</span>
          <span style={{fontFamily:"var(--p-outfit)",fontSize:".68rem",color:"var(--p-muted)"}}>
            {readingTime} min read
            {(story.commentCount||0)>0&&<> · 💬 {story.commentCount}</>}
          </span>
          {!isSelf&&(
            <button onClick={handleReport} style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",fontSize:".8rem",opacity:.2,padding:0,transition:"opacity .15s"}} onMouseEnter={e=>e.currentTarget.style.opacity=".75"} onMouseLeave={e=>e.currentTarget.style.opacity=".2"}>🚩</button>
          )}
        </div>

        <h3 className="p-t-heading"
          onClick={()=>onOpenModal(story)}
          style={{fontSize:"1.05rem",cursor:"pointer",margin:0,transition:"color .15s"}}
          onMouseEnter={e=>e.currentTarget.style.color=theme.color}
          onMouseLeave={e=>e.currentTarget.style.color="var(--p-ink)"}>
          {story.title}
        </h3>

        <p className="p-t-body" style={{margin:0,display:"-webkit-box",WebkitLineClamp:3,WebkitBoxOrient:"vertical",overflow:"hidden"}}>
          {preview}…
        </p>

        <button onClick={()=>onOpenModal(story)}
          style={{alignSelf:"flex-start",background:"none",border:"none",cursor:"pointer",
            fontFamily:"var(--p-outfit)",fontSize:".78rem",fontWeight:700,color:theme.color,
            padding:0,letterSpacing:".02em",transition:"opacity .15s"}}
          onMouseEnter={e=>e.currentTarget.style.opacity=".65"}
          onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
          Read story →
        </button>

        <div style={{display:"flex",gap:6}}>
          <button onClick={()=>handleVote("like")} className={`p-card-react${myVote==="like"?" liked":""}${likeAnim?" heart-pulse":""}`} style={{"--react-c":theme.color,"--react-l":theme.light}}>
            {myVote==="like"?"❤️":"🤍"} <span style={{minWidth:12,textAlign:"center",display:"inline-block"}}>{likes}</span>
          </button>
          <button onClick={()=>handleVote("dislike")} className={`p-card-react${myVote==="dislike"?" disliked":""}`}>
            👎 <span style={{minWidth:12,textAlign:"center",display:"inline-block"}}>{dislikes}</span>
          </button>
        </div>

        <div style={{marginTop:"auto",paddingTop:12,borderTop:"1px solid var(--p-rule-s)",
          display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
          <div style={{display:"flex",alignItems:"center",gap:9}}>
            <div style={{width:28,height:28,borderRadius:"50%",background:theme.light,color:theme.color,
              display:"flex",alignItems:"center",justifyContent:"center",
              fontFamily:"var(--p-cormorant)",fontWeight:700,fontSize:".82rem",flexShrink:0}}>
              {(story.authorName||"A")[0].toUpperCase()}
            </div>
            <div>
              <div style={{fontFamily:"var(--p-outfit)",fontSize:".78rem",fontWeight:700,color:"var(--p-ink)"}}>
                {story.authorName||"Anonymous"}
              </div>
              <div style={{fontFamily:"var(--p-outfit)",fontSize:".66rem",color:"var(--p-muted)"}}>
                {fCount>0?`${fCount} follower${fCount!==1?"s":""} · `:""}{dateStr}
              </div>
            </div>
          </div>
          {!isSelf&&story.authorId&&(
            <button onClick={()=>{if(!followLoggedIn){alert("Please log in.");return;}toggleFollow();}}
              className="p-follow"
              style={{border:following?`1.5px solid ${theme.color}`:"1px solid var(--p-rule)",
                background:following?theme.light:"var(--p-surface)",
                color:following?theme.color:"var(--p-ink-3)",
                fontSize:".72rem",padding:"4px 12px"}}>
              {following?"✓":"+"} 
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   MODERATOR DASHBOARD — unchanged
══════════════════════════════════════════════ */
function ModeratorDashboard({onBack}){
  const [reports,setReports]=useState([]);
  const [loading,setLoading]=useState(true);
  useEffect(()=>{
    async function load(){
      try{
        const q=query(collection(db,"reports"),where("status","==","pending"),orderBy("timestamp","desc"));
        const snap=await getDocs(q);
        const docs=snap.docs.map(d=>({reportId:d.id,...d.data()}));
        const snaps=await Promise.all(docs.map(r=>getDoc(doc(db,"stories",r.storyId))));
        setReports(docs.map((r,i)=>({...r,story:snaps[i].exists()?{id:snaps[i].id,...snaps[i].data()}:null})));
      }catch(e){console.error(e);}
      finally{setLoading(false);}
    }
    load();
  },[]);
  async function delStory(storyId,reportId){
    if(!window.confirm("Delete story permanently?"))return;
    try{await deleteDoc(doc(db,"stories",storyId));await updateDoc(doc(db,"reports",reportId),{status:"resolved",resolvedAt:serverTimestamp()});setReports(p=>p.filter(r=>r.reportId!==reportId));}
    catch(e){console.error(e);}
  }
  async function dismiss(reportId){
    try{await updateDoc(doc(db,"reports",reportId),{status:"dismissed",resolvedAt:serverTimestamp()});setReports(p=>p.filter(r=>r.reportId!==reportId));}
    catch(e){console.error(e);}
  }
  return(
    <div style={{maxWidth:860,margin:"0 auto",padding:"36px 20px",fontFamily:"var(--p-outfit)"}}>
      <header style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:36,flexWrap:"wrap",gap:14}}>
        <div>
          <h2 className="p-t-display" style={{fontSize:"2rem",marginBottom:6}}>Moderation Queue</h2>
          <p className="p-t-body">Review reported stories and maintain community standards.</p>
        </div>
        <button onClick={onBack} className="p-btn p-btn-ghost" style={{fontSize:".83rem"}}>← Back to Feed</button>
      </header>
      {loading?(
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {[1,2].map(i=><div key={i} className="p-skel" style={{height:200,borderRadius:"var(--p-r-md)"}}/>)}
        </div>
      ):reports.length===0?(
        <div style={{textAlign:"center",padding:"80px 0",background:"var(--p-surface)",borderRadius:"var(--p-r-lg)",border:"1px solid var(--p-rule)",boxShadow:"var(--p-sh-sm)"}}>
          <div style={{fontSize:"3rem",marginBottom:16}}>✅</div>
          <h3 className="p-t-display" style={{fontSize:"1.6rem",marginBottom:8}}>Queue Clear</h3>
          <p className="p-t-body">No pending reports. The community is healthy.</p>
        </div>
      ):(
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {reports.map(item=>{
            const t=STORY_THEMES.find(t=>t.id===item.story?.theme);
            return(
              <div key={item.reportId} style={{background:"var(--p-surface)",border:"1px solid #FECACA",borderRadius:"var(--p-r-md)",overflow:"hidden",boxShadow:"0 2px 14px rgba(153,27,27,.06)"}}>
                <div style={{padding:"9px 18px",background:"#FEF2F2",borderBottom:"1px solid #FECACA",display:"flex",justifyContent:"space-between",fontSize:".7rem",color:"#991B1B",fontWeight:700,letterSpacing:".05em",textTransform:"uppercase"}}>
                  <span>🚩 Report · {item.reporterId}</span>
                  <span>{item.timestamp?.toDate().toLocaleDateString()}</span>
                </div>
                <div style={{padding:"20px 22px 24px"}}>
                  {item.story?(
                    <>
                      {t&&<span style={{display:"inline-flex",alignItems:"center",gap:4,padding:"2px 10px",borderRadius:100,background:t.light,color:t.color,fontFamily:"var(--p-outfit)",fontSize:".62rem",fontWeight:700,letterSpacing:".06em",textTransform:"uppercase",marginBottom:10}}>{t.emoji} {t.label}</span>}
                      <h3 className="p-t-heading" style={{fontSize:"1.1rem",marginBottom:12,marginTop:8}}>{item.story.title}</h3>
                      <div style={{fontSize:".87rem",lineHeight:1.65,color:"var(--p-ink-3)",maxHeight:130,overflowY:"auto",background:"var(--p-warm)",padding:"12px 16px",borderRadius:"var(--p-r-sm)",border:"1px solid var(--p-rule)",fontFamily:"var(--p-outfit)"}}
                        dangerouslySetInnerHTML={{__html:sanitize(item.story.content)}}/>
                    </>
                  ):(
                    <div style={{padding:20,background:"var(--p-warm)",borderRadius:"var(--p-r-sm)",color:"var(--p-muted)",textAlign:"center",fontFamily:"var(--p-outfit)"}}>Story no longer exists.</div>
                  )}
                  <div style={{display:"flex",gap:12,marginTop:18}}>
                    <button onClick={()=>delStory(item.storyId,item.reportId)}
                      style={{flex:1,padding:"10px",borderRadius:"var(--p-r-sm)",border:"none",fontFamily:"var(--p-outfit)",background:"#991B1B",color:"#fff",fontWeight:700,fontSize:".86rem",cursor:"pointer",transition:"background .15s"}}
                      onMouseEnter={e=>e.currentTarget.style.background="#7F1D1D"}
                      onMouseLeave={e=>e.currentTarget.style.background="#991B1B"}>
                      Delete Story
                    </button>
                    <button onClick={()=>dismiss(item.reportId)}
                      style={{flex:1,padding:"10px",borderRadius:"var(--p-r-sm)",border:"1px solid var(--p-rule)",fontFamily:"var(--p-outfit)",background:"var(--p-surface)",color:"var(--p-ink)",fontWeight:700,fontSize:".86rem",cursor:"pointer"}}>
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   WRITE STORY FORM — unchanged logic
══════════════════════════════════════════════ */
function WriteStoryForm({onSuccess,onCancel}){
  const [title,setTitle]=useState("");
  const [theme,setTheme]=useState("");
  const [content,setContent]=useState("");
  const [authorName,setAuthorName]=useState("");
  const [nameLoading,setNameLoading]=useState(true);
  const [nameSaving,setNameSaving]=useState(false);
  const [nameSaved,setNameSaved]=useState(false);
  const [submitting,setSubmitting]=useState(false);
  const [error,setError]=useState("");
  const [flagged,setFlagged]=useState([]);
  const [draftSaved,setDraftSaved]=useState(false);
  const [draftContent,setDraftContent]=useState("");

  const user=auth.currentUser;
  const selectedTheme=STORY_THEMES.find(t=>t.id===theme);
  const wordCount=countWords(content);
  const isOver=wordCount>1000;
  const nameOk=authorName.trim().length>=2;
  const canSubmit=!submitting&&!isOver&&flagged.length===0&&nameOk;

  useEffect(()=>{
    async function loadName(){
      if(!user){setNameLoading(false);return;}
      try{const s=await getDoc(doc(db,"users",user.uid));if(s.exists()&&s.data().name)setAuthorName(s.data().name);else if(user.displayName)setAuthorName(user.displayName);}
      catch(e){console.error(e);}
      finally{setNameLoading(false);}
    }
    loadName();
  // eslint-disable-next-line
  },[]);

  async function saveName(name){
    if(!user||!name.trim()||nameSaving)return;
    setNameSaving(true);
    try{await setDoc(doc(db,"users",user.uid),{name:name.trim()},{merge:true});setNameSaved(true);setTimeout(()=>setNameSaved(false),2000);}
    catch(e){console.error(e);}
    finally{setNameSaving(false);}
  }

  useEffect(()=>{
    try{const d=localStorage.getItem("healhub_storyDraft");if(d){const p=JSON.parse(d);if(p.title)setTitle(p.title);if(p.theme)setTheme(p.theme);if(p.content){setDraftContent(p.content);setContent(p.content);setFlagged(containsBannedWords(p.content));}}}catch{}
  },[]);

  useEffect(()=>{
    const t=setTimeout(()=>{
      try{localStorage.setItem("healhub_storyDraft",JSON.stringify({title,theme,content}));if(content.trim()){setDraftSaved(true);setTimeout(()=>setDraftSaved(false),1500);}}catch{}
    },2000);
    return()=>clearTimeout(t);
  },[title,theme,content]);

  function handleContentChange(html){setContent(html);setFlagged(containsBannedWords(html));}

  async function handleSubmit(){
    setError("");
    if(!nameOk)        {setError("Please enter your display name (at least 2 characters).");return;}
    if(!title.trim())  {setError("Please add a title.");return;}
    if(!theme)         {setError("Please select a category.");return;}
    if(wordCount<50)   {setError("Story must be at least 50 words.");return;}
    if(isOver)         {setError("Story exceeds 1000 words. Please trim it down.");return;}
    if(flagged.length) {setError(`Inappropriate language: "${flagged.join('", "')}". Remove before posting.`);return;}
    if(!user)          {setError("You must be logged in to post.");return;}
    setSubmitting(true);
    try{
      await setDoc(doc(db,"users",user.uid),{name:authorName.trim()},{merge:true});
      await addDoc(collection(db,"stories"),{title:title.trim(),theme,content:sanitize(content),authorName:authorName.trim(),authorId:user.uid,wordCount,likes:0,dislikes:0,commentCount:0,createdAt:serverTimestamp()});
      localStorage.removeItem("healhub_storyDraft");
      onSuccess();
    }catch(e){console.error(e);setError("Failed to publish. Please try again.");}
    finally{setSubmitting(false);}
  }

  const bgTransition=selectedTheme
    ?{background:`linear-gradient(180deg, ${selectedTheme.editorBg} 0%, var(--p-bg) 55%)`}
    :{background:"var(--p-bg)"};

  return(
    <div style={{...bgTransition,minHeight:"60vh",transition:"background .6s ease"}}>
      <div style={{maxWidth:760,margin:"0 auto",padding:"40px 20px 70px",display:"flex",flexDirection:"column",gap:28}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12}}>
          <div>
            <h2 className="p-t-display" style={{fontSize:"2.2rem",marginBottom:6}}>Write Your Story</h2>
            <p className="p-t-body" style={{fontStyle:"italic"}}>Authentic voices only. No AI-generated content.</p>
          </div>
          {draftSaved&&(
            <span style={{fontFamily:"var(--p-outfit)",fontSize:".75rem",color:"var(--p-muted)",display:"flex",alignItems:"center",gap:6}}>
              <span style={{width:5,height:5,borderRadius:"50%",background:"#3D6B57"}}/>
              Draft saved
            </span>
          )}
        </div>

        <div>
          <label className="p-t-label" style={{display:"block",marginBottom:8}}>Your name</label>
          {nameLoading?(
            <div className="p-skel" style={{height:50,borderRadius:"var(--p-r-sm)"}}/>
          ):(
            <div style={{position:"relative"}}>
              <input value={authorName} onChange={e=>setAuthorName(e.target.value)}
                onBlur={()=>{if(authorName.trim().length>=2)saveName(authorName);}}
                placeholder="Your name or pen name…" maxLength={50} className="p-input"
                style={{fontSize:"1rem",fontWeight:600,paddingRight:44,
                  borderColor:!authorName.trim()?"#FECACA":"var(--p-rule)"}}/>
              <span style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",fontSize:".88rem"}}>
                {nameSaving?"⏳":nameSaved?"✓":nameOk?"✓":""}
              </span>
            </div>
          )}
          {!nameLoading&&!authorName.trim()&&<p style={{margin:"5px 0 0",fontFamily:"var(--p-outfit)",fontSize:".78rem",color:"#991B1B"}}>A display name is required.</p>}
        </div>

        <div>
          <label className="p-t-label" style={{display:"block",marginBottom:10}}>Title</label>
          <input value={title} onChange={e=>setTitle(e.target.value)}
            placeholder="The title of your story…" maxLength={120}
            style={{width:"100%",fontFamily:"var(--p-cormorant)",fontSize:"1.75rem",fontWeight:700,
              letterSpacing:"-.02em",lineHeight:1.2,padding:"14px 0",
              border:"none",borderBottom:"1.5px solid var(--p-rule)",
              background:"transparent",color:"var(--p-ink)",outline:"none",
              boxSizing:"border-box",caretColor:"var(--p-ink)",
              transition:"border-color .2s"}}
            onFocus={e=>e.currentTarget.style.borderBottomColor="var(--p-ink-3)"}
            onBlur={e=>e.currentTarget.style.borderBottomColor="var(--p-rule)"}/>
        </div>

        <div>
          <label className="p-t-label" style={{display:"block",marginBottom:10}}>Category</label>
          {/* className="p-pill-row" enables mobile horizontal scroll */}
          <div className="p-pill-row" style={{display:"flex",flexWrap:"wrap",gap:7}}>
            {STORY_THEMES.map(t=>(
              <button key={t.id} onClick={()=>setTheme(t.id)}
                className="p-pill"
                style={theme===t.id?{"--pill-c":t.color,background:t.color,borderColor:t.color,color:"#fff",fontWeight:700}:{}}>
                {t.emoji} {t.label}
              </button>
            ))}
          </div>
          {selectedTheme&&(
            <div style={{marginTop:14,padding:"12px 16px",borderRadius:"var(--p-r-sm)",
              background:selectedTheme.light,border:`1px solid ${selectedTheme.color}22`,
              display:"flex",gap:10,alignItems:"center"}}>
              <div style={{width:32,height:32,borderRadius:8,flexShrink:0,overflow:"hidden"}}>
                <ThemeArt theme={selectedTheme} height={32}/>
              </div>
              <span style={{fontFamily:"var(--p-outfit)",fontSize:".8rem",color:selectedTheme.textColor,fontWeight:500}}>
                Writing in <strong>{selectedTheme.label}</strong> — editor will reflect this theme
              </span>
            </div>
          )}
        </div>

        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <label className="p-t-label">Story content <span style={{fontWeight:400,textTransform:"none",letterSpacing:0,color:"var(--p-muted)",fontSize:".78rem"}}>(50–1000 words)</span></label>
          </div>
          <RichEditor onChange={handleContentChange} theme={selectedTheme} initialContent={draftContent}/>
        </div>

        {flagged.length>0&&(
          <div className="p-notice err">⚠️ Flagged language: "{flagged.join('", "')}" — remove before posting.</div>
        )}
        {error&&<div className="p-notice err">{error}</div>}

        <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
          <button onClick={handleSubmit} disabled={!canSubmit} className="p-btn p-btn-ink">
            {submitting?"Publishing…":"Publish Story →"}
          </button>
          <button onClick={onCancel} className="p-btn p-btn-ghost">Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   MAIN EXPORT
   Mobile changes:
   - className="p-feed-header" on top bar → CSS stacks it on mobile
   - className="p-pill-row" on theme filter row → CSS scrollable on mobile
   - className="p-search-row" on search+sort row → CSS stacks on mobile
══════════════════════════════════════════════ */
export default function StoryWriter(){
  useEffect(()=>{injectStyles();},[]);
  const isMobile=useIsMobile();

  const [view,setView]=useState("browse");
  const [feedTab,setFeedTab]=useState("all");
  const [stories,setStories]=useState([]);
  const [loading,setLoading]=useState(true);
  const [activeTheme,setActiveTheme]=useState("all");
  const [sortBy,setSortBy]=useState("newest");
  const [searchQuery,setSearchQuery]=useState("");
  const [successMsg,setSuccessMsg]=useState("");
  const [lastDoc,setLastDoc]=useState(null);
  const [hasMore,setHasMore]=useState(true);
  const [isAdmin,setIsAdmin]=useState(false);
  const [modalStory,setModalStory]=useState(null);
  const [showScrollTop,setShowScrollTop]=useState(false);

  const {bookmarks,toggle:toggleBookmark}=useBookmarks();
  const uid=auth.currentUser?.uid;

  useEffect(()=>{const fn=()=>setShowScrollTop(window.scrollY>400);window.addEventListener("scroll",fn);return()=>window.removeEventListener("scroll",fn);},[]);
  useEffect(()=>{if(!uid)return;getDoc(doc(db,"users",uid)).then(s=>{if(s.exists()&&s.data().role==="admin")setIsAdmin(true);}).catch(()=>{});},[uid]);

  const fetchStories=useCallback(async(loadMore=false,lastDocOverride=null,hasMoreOverride=true)=>{
    if(!hasMoreOverride&&loadMore)return;
    setLoading(true);
    try{
      const eld=loadMore?lastDocOverride:null;
      let q;
      if(activeTheme==="all"){q=eld?query(collection(db,"stories"),orderBy("createdAt","desc"),startAfter(eld),limit(PAGE_SIZE)):query(collection(db,"stories"),orderBy("createdAt","desc"),limit(PAGE_SIZE));}
      else{q=eld?query(collection(db,"stories"),where("theme","==",activeTheme),orderBy("createdAt","desc"),startAfter(eld),limit(PAGE_SIZE)):query(collection(db,"stories"),where("theme","==",activeTheme),orderBy("createdAt","desc"),limit(PAGE_SIZE));}
      const snap=await getDocs(q);
      const docs=snap.docs.map(d=>({id:d.id,...d.data()}));
      setLastDoc(snap.docs[snap.docs.length-1]??null);
      setHasMore(snap.docs.length===PAGE_SIZE);
      if(loadMore)setStories(p=>[...p,...docs]);else setStories(docs);
    }catch(e){console.error(e);}
    finally{setLoading(false);}
  },[activeTheme]);

  useEffect(()=>{setLastDoc(null);setHasMore(true);setStories([]);fetchStories(false,null,true);},[activeTheme]); // eslint-disable-line

  const handleDeleteLocally=id=>{setStories(p=>p.filter(s=>s.id!==id));if(modalStory?.id===id)setModalStory(null);};

  const displayedStories=useMemo(()=>{
    let list=[...stories];
    if(feedTab==="bookmarks")list=list.filter(s=>bookmarks.has(s.id));
    if(feedTab==="following"){
      try{const f=new Set(JSON.parse(localStorage.getItem("hh_following")||"[]"));list=list.filter(s=>f.has(s.authorId));}
      catch{list=[];}
    }
    if(searchQuery.trim()){const q=searchQuery.toLowerCase().trim();list=list.filter(s=>s.title?.toLowerCase().includes(q)||s.authorName?.toLowerCase().includes(q));}
    if(sortBy==="mostLiked")list.sort((a,b)=>(b.likes||0)-(a.likes||0));
    else if(sortBy==="mostRead")list.sort((a,b)=>(b.wordCount||0)-(a.wordCount||0));
    return list;
  },[stories,feedTab,bookmarks,searchQuery,sortBy]);

  const featuredStory=useMemo(()=>{
    if(!displayedStories.length)return null;
    return [...displayedStories].sort((a,b)=>(b.likes||0)-(a.likes||0))[0];
  },[displayedStories]);

  const gridStories=useMemo(()=>{
    if(!featuredStory)return displayedStories;
    return displayedStories.filter(s=>s.id!==featuredStory.id);
  },[displayedStories,featuredStory]);

  const GrainOverlay=()=><div className="p-grain" aria-hidden="true"/>;

  if(view==="write"){
    return(
      <>
        <GrainOverlay/>
        <WriteStoryForm
          onSuccess={()=>{setView("browse");setSuccessMsg("Your story has been published.");setLastDoc(null);setHasMore(true);fetchStories(false,null,true);setTimeout(()=>setSuccessMsg(""),5000);}}
          onCancel={()=>setView("browse")}/>
      </>
    );
  }
  if(view==="mod")return(<><GrainOverlay/><ModeratorDashboard onBack={()=>setView("browse")}/></>);

  const today=new Date().toLocaleDateString("en-IN",{weekday:"long",day:"numeric",month:"long",year:"numeric"});

  return(
    <div style={{fontFamily:"var(--p-outfit)",background:"var(--p-bg)",minHeight:"100vh"}}>
      <GrainOverlay/>

      {/* Masthead — className="p-feed-header" enables mobile stack */}
      <div style={{paddingBottom:20,marginBottom:6}}>
        <div className="p-feed-header" style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,flexWrap:"wrap",gap:8}}>
          <span style={{fontFamily:"var(--p-outfit)",fontSize:".65rem",fontWeight:600,
            letterSpacing:".1em",textTransform:"uppercase",color:"var(--p-muted)"}}>
            {today}
          </span>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <button onClick={()=>{setView("write");setSuccessMsg("");}}
              className="p-btn p-btn-ink" style={{padding:"9px 22px",fontSize:".84rem"}}>
              ✍️ Write a story
            </button>
            {isAdmin&&(
              <button onClick={()=>{setView("mod");setSuccessMsg("");}}
                className="p-btn p-btn-ghost" style={{padding:"9px 18px",fontSize:".84rem"}}>
                🛡️
              </button>
            )}
          </div>
        </div>

        <div className="p-masthead-rule">
          <span style={{fontFamily:"var(--p-cormorant)",fontSize:"clamp(1.8rem,5vw,2.8rem)",
            fontWeight:700,letterSpacing:"-.02em",color:"var(--p-ink)",lineHeight:1,whiteSpace:"nowrap"}}>
            HealHub
          </span>
          <span style={{fontFamily:"var(--p-outfit)",fontSize:".72rem",fontWeight:400,
            color:"var(--p-muted)",letterSpacing:".04em",fontStyle:"italic",whiteSpace:"nowrap"}}>
            A journal of human experience
          </span>
        </div>
      </div>

      {successMsg&&(
        <div className="p-notice ok" style={{marginBottom:20,animation:"pFadeUp .3s ease both"}}>
          ✓ {successMsg}
        </div>
      )}

      {/* Feed tabs */}
      <div style={{display:"flex",gap:8,marginBottom:18,flexWrap:"wrap"}}>
        {[{key:"all",label:"All Stories"},{key:"following",label:"Following"},{key:"bookmarks",label:`Saved${bookmarks.size>0?` (${bookmarks.size})`:""}`}].map(tab=>(
          <button key={tab.key} className={`p-tab${feedTab===tab.key?" on":""}`} onClick={()=>setFeedTab(tab.key)}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Theme filter — className="p-pill-row" enables horizontal scroll on mobile */}
      <div className="p-pill-row" style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:16,paddingBottom:16,borderBottom:"1px solid var(--p-rule)"}}>
        <button className={`p-pill${activeTheme==="all"?" on":""}`}
          style={activeTheme==="all"?{"--pill-c":"var(--p-ink)"}:{}}
          onClick={()=>setActiveTheme("all")}>
          All
        </button>
        {STORY_THEMES.map(t=>(
          <button key={t.id} className={`p-pill${activeTheme===t.id?" on":""}`}
            style={activeTheme===t.id?{"--pill-c":t.color}:{}}
            onClick={()=>setActiveTheme(t.id)}>
            {t.emoji} {t.label}
          </button>
        ))}
      </div>

      {/* Search + sort — className="p-search-row" enables mobile stack */}
      <div className="p-search-row" style={{display:"flex",alignItems:"center",gap:10,marginBottom:28,flexWrap:"wrap"}}>
        <div style={{position:"relative",flex:1,minWidth:0}}>
          <span style={{position:"absolute",left:13,top:"50%",transform:"translateY(-50%)",fontSize:".8rem",color:"var(--p-muted)",pointerEvents:"none"}}>🔍</span>
          <input className="p-search" value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} placeholder="Search stories or authors…"/>
          {searchQuery&&<button onClick={()=>setSearchQuery("")} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",fontSize:".78rem",color:"var(--p-muted)",padding:2}}>✕</button>}
        </div>
        <select value={sortBy} onChange={e=>setSortBy(e.target.value)} className="p-select">
          <option value="newest">Newest first</option>
          <option value="mostLiked">Most liked</option>
          <option value="mostRead">Longest read</option>
        </select>
        {searchQuery&&<span style={{fontFamily:"var(--p-outfit)",fontSize:".8rem",color:"var(--p-muted)",fontStyle:"italic"}}>{displayedStories.length} result{displayedStories.length!==1?"s":""}</span>}
      </div>

      {/* Content */}
      {loading&&stories.length===0?(
        <>
          {!isMobile&&(
            <div style={{display:"grid",gridTemplateColumns:"1.2fr 1fr",gap:20,marginBottom:24}}>
              <div className="p-skel" style={{height:320,borderRadius:"var(--p-r-lg)"}}/>
              <div className="p-skel" style={{height:320,borderRadius:"var(--p-r-lg)"}}/>
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fill,minmax(280px,1fr))",gap:20}}>
            {[1,2,3,4].map(i=><SkeletonCard key={i}/>)}
          </div>
        </>
      ):displayedStories.length===0?(
        <div style={{textAlign:"center",padding:"80px 0",color:"var(--p-muted)"}}>
          <div style={{fontSize:"3rem",marginBottom:16}}>
            {feedTab==="bookmarks"?"🔖":feedTab==="following"?"👥":"📭"}
          </div>
          <p style={{fontFamily:"var(--p-cormorant)",fontSize:"1.3rem",fontStyle:"italic",color:"var(--p-ink-3)"}}>
            {feedTab==="bookmarks"?"No saved stories yet."
              :feedTab==="following"?"Follow some authors to see their stories here."
              :searchQuery?`No stories match "${searchQuery}".`
              :activeTheme==="all"?"No stories yet. Be the first."
              :"No stories in this category yet."}
          </p>
        </div>
      ):(
        <>
          {featuredStory&&!searchQuery&&feedTab==="all"&&(
            <div style={{marginBottom:24}}>
              <StoryCard story={featuredStory} onDelete={handleDeleteLocally} isAdmin={isAdmin}
                bookmarks={bookmarks} onBookmarkToggle={toggleBookmark} onOpenModal={setModalStory} featured/>
            </div>
          )}

          {(()=>{
            const list=searchQuery||feedTab!=="all"?displayedStories:gridStories;
            if(!list.length)return null;
            const rows=[];
            let i=0;
            // Row 0: 1-col on mobile, 3-col on desktop
            if(i<list.length){
              const rowCards=list.slice(i,i+3); i+=3;
              rows.push(
                <div key="r0" style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fill,minmax(280px,1fr))",gap:20,marginBottom:20}}>
                  {rowCards.map(s=><StoryCard key={s.id} story={s} onDelete={handleDeleteLocally} isAdmin={isAdmin} bookmarks={bookmarks} onBookmarkToggle={toggleBookmark} onOpenModal={setModalStory}/>)}
                </div>
              );
            }
            // Row 1: 1-col on mobile, asymmetric 2-col on desktop
            if(i<list.length){
              const rowCards=list.slice(i,i+2); i+=2;
              rows.push(
                <div key="r1" style={{display:"grid",gridTemplateColumns:isMobile?"1fr":rowCards.length>1?"1.3fr 1fr":"1fr",gap:20,marginBottom:20}}>
                  {rowCards.map(s=><StoryCard key={s.id} story={s} onDelete={handleDeleteLocally} isAdmin={isAdmin} bookmarks={bookmarks} onBookmarkToggle={toggleBookmark} onOpenModal={setModalStory}/>)}
                </div>
              );
            }
            // Remaining: standard grid
            if(i<list.length){
              rows.push(
                <div key="rest" style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fill,minmax(280px,1fr))",gap:20}}>
                  {list.slice(i).map(s=><StoryCard key={s.id} story={s} onDelete={handleDeleteLocally} isAdmin={isAdmin} bookmarks={bookmarks} onBookmarkToggle={toggleBookmark} onOpenModal={setModalStory}/>)}
                </div>
              );
            }
            return rows;
          })()}
        </>
      )}

      {hasMore&&stories.length>0&&!searchQuery&&feedTab==="all"&&(
        <div style={{textAlign:"center",marginTop:44}}>
          <button onClick={()=>fetchStories(true,lastDoc,hasMore)} disabled={loading}
            className="p-btn p-btn-ghost" style={{fontSize:".86rem",opacity:loading?.5:1}}>
            {loading?"Loading…":"Load more stories"}
          </button>
        </div>
      )}

      {modalStory&&(
        <StoryModal story={modalStory} onClose={()=>setModalStory(null)} isAdmin={isAdmin} onDelete={handleDeleteLocally}/>
      )}

      {showScrollTop&&(
        <button className="p-scroll-top" onClick={()=>window.scrollTo({top:0,behavior:"smooth"})} title="Back to top">↑</button>
      )}
    </div>
  );
}